<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref, inject, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import cloneDeep from 'lodash/cloneDeep';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { TOTAL_COST, UNASSIGNED, HOTEL_COST_ADJ } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useWorksheetComposable } from './useWorksheetComposable';
import { useComponentComposable } from '@/components/useComponentComposable';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useSubmitWorksheetStore } from '@/store/SubmitWorksheetStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import DataTable, { DataTableCellEditCancelEvent } from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import RadioButton from 'primevue/radiobutton';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import AdjustOptionMarginModal from '@/components/modal/worksheet/AdjustOptionMarginModal.vue';
import QuoteAdjustmentToolModal from '@/components/modal/worksheet/QuoteAdjustmentToolModal.vue';
import RunOfHouseSelection from './RunOfHouseSelection.vue';
import ViewFlightSeat from './ViewFlightSeat.vue';
import ViewPricingModal from './ViewPricingModal.vue';
import ViewPDFFile from '@/components/modal/utils/ViewPDFFile.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import LinkedQuotesEditor from '@/components/modal/worksheet/LinkedQuotesEditor.vue';
import PriceBreakdownModal from '@/components/modal/PriceBreakdownModal.vue';

const {
  filterPriceList,
  PRICE_KEY,
  titleHotel,
  authorizations,
  canViewPromotion,
  canEditPromotion,
  canViewAgencyComment,
  canEditAgencyComment,
  canViewTermConditions,
  canEditTermConditions,
  canAdjustOptionMargin,
  hasUserPermission,
  loadingIcon,
  validateFlightNumber,
  airportList,
  initFlightData,
  formatFlightData,
  onFilterAirport,
  LIST_CABIN,
  getCabinName,
  formatDataBeforeSaveRow,
  semistarImage,
  handleRedirectLink,
  isCheckValidateDate,
  handleConvertBrTag,
  handleChildInput,
  checkEditChildInput,
  isInvalidValuePrice
} = useWorksheetComposable();

const dialogRef = inject('dialogRef') as Ref;
const reloadList = dialogRef.value.options.emits.reloadList;

const { t } = useI18n({ useScope: 'global' });
const lang = getCurrentLocale();
const dialog = useDialog();
const toast = useToast();
const packageStore = usePackageManagementStore();
const adminQuoteStore = useAdminQuoteStore();
const submitWorksheetStore = useSubmitWorksheetStore();
const airManagementStore = useAirManagementStore();
const { fallBackImage } = useComponentComposable();
const editingFlightRows = ref([]);
const visible = ref(false);
const isPromotionModal = ref(false);
const editingRowsHotel = ref({});
const editingRowsHotelPrefer = ref({});
const filterGateway = ref([]);
const filterDestination = ref([]);
const airlineList = ref([]);
const isLoadingButton = ref(false);
const isLoadingTool = ref(false);
const loadingWorksheetPackage = ref(false);
const cabins = ref('');
const segmentGroups = ref([]);
const adtNo = ref(0);
const chdNo = ref(0);
const getTaxes = ref<any>([]);
const getTaxes346 = ref();
const classOutbound = ref([]);
const classInbound = ref([]);
const listPriceFilter = ref([]);
const numberDynamicColumns = ref<any>([]);
const isSearchAdditional = ref(false);
const disablePreviewPDF = ref(true);
const worksheetPrices = ref<any>([]);
const oldChildValue = ref([]);
const roomAdjustmentInfo = ref([]);
const currentOption = ref(0);
const newPrice = ref(false);
const newHcms = ref(false);
const errorMessage = ref('');
const isEmptyRespond = ref(false);
const isLoading = ref(false);
const canViewFlight = authorizations?.worksheet_package?.flight_view;
const canEditFlight = authorizations?.worksheet_package?.flight_edit;
const canEditHeader = authorizations?.worksheet_package?.quote_details_header_edit;
const canEditRoh = authorizations?.worksheet_package?.roh_edit;
const priceDetail = ref({
  currency: '',
  rateOfExchange: 1
});
const flightVendorList = ['VAC', 'GROUPSB', 'PUBLISHED'];
const doublePricePublic = ref<any>([]);
const isShowError = ref(false);
const validationInputs = reactive<Record<string, boolean>>({});

const newDataPricing = reactive({
  packageIndex: '',
  hotelIndex: '',
  hotelType: '',
  hotelData: '',
  roomData: '',
  promotions: '',
  additionalHotelDetail: '',
  flightData: ''
});

const ardBaseFareObj = {
  childPrices: ['0.00'],
  singlePrice: '0.00',
  doublePrice: '0.00',
  triplePrice: '0.00',
  quadPrice: '0.00',
  fiveInRoomPrice: '0.00',
  sixInRoomPrice: '0.00',
  infantPrice: '0.00',
  childOnePrice: '0.00',
  childTwoPrice: '0.00',
  childThreePrice: '0.00'
};

const ardTaxesObj = {
  singlePrice: '0.00',
  doublePrice: '0.00',
  triplePrice: '0.00',
  quadPrice: '0.00',
  fiveInRoomPrice: '0.00',
  sixInRoomPrice: '0.00',
  infantPrice: '0.00',
  childOnePrice: '0.00',
  childTwoPrice: '0.00',
  childThreePrice: '0.00'
};

const netMarginObj = {
  singlePrice: '0.00',
  doublePrice: '0.00',
  triplePrice: '0.00',
  quadPrice: '0.00',
  fiveInRoomPrice: '0.00',
  sixInRoomPrice: '0.00',
  infantPrice: '0.00',
  childOnePrice: '0.00',
  childTwoPrice: '0.00',
  childThreePrice: '0.00'
};

const serviceFlightType = {
  outbound: 'Outbound',
  inbound: 'Inbound'
};

const serviceFlight = [{ type: 'Economy' }, { type: 'Business' }];

const promoEditedData = ref({
  index: '',
  data: ''
});

const editComment = ref({
  index: '',
  data: ''
});

const editAdditionalHotel = ref({
  index: '',
  data: ''
});

const editPromoHotelPrefer = ref({
  index: '',
  data: ''
});

const editIntro = ref({
  isEdit: false,
  data: ''
});

const editTermConditions = ref({
  isEdit: false,
  data: ''
});

const editCommentHotelPrefer = ref({
  index: '',
  data: ''
});

const packageData = reactive({
  depDate: '',
  duration: ''
});

const editExpiryDate = ref({
  isEdit: false,
  data: null
});

const editAdult = ref({
  isEdit: false,
  data: ''
});

const editChild = ref({
  isEdit: false,
  data: ''
});

const editNumberOfRoom = ref({
  isEdit: false,
  data: ''
});

const editDateBooked = ref({
  isEdit: false,
  data: null
});

const editHotelRoomCategoryPackage = ref({
  index: '',
  data: ''
});

const flightVendor = ref('VAC');

const packageWorkSheet = computed(() => {
  return adminQuoteStore.currentQuote;
});

const isIncentiveGroupType = computed(() => {
  return packageWorkSheet.value.gType.includes('Incentive');
});

const today = new Date(packageWorkSheet.value?.created);
today.setDate(today.getDate() + 346);

const getTotalAdtFare = (index: number) => {
  const taxValue = getTaxes.value[index];
  let total = 0;
  if (taxValue.length) {
    total = taxValue.reduce(function (acc, obj) {
      return acc + (obj?.adtFare && obj?.airTaxCode != 'FARE_BASE' ? +obj?.adtFare : 0);
    }, 0);
  }
  return total.toFixed(2);
};

const getTotalChFare = (index: number) => {
  const taxValue = getTaxes.value[index];
  let total = 0;
  if (taxValue.length) {
    total = taxValue.reduce(function (acc, obj) {
      return acc + (obj?.chFare && obj?.airTaxCode != 'FARE_BASE' ? +obj?.chFare : 0);
    }, 0);
  }
  return total.toFixed(2);
};

const getTotalInFare = (index: number) => {
  const taxValue = getTaxes.value[index];
  let total = 0;
  if (taxValue.length) {
    total = taxValue.reduce(function (acc, obj) {
      return acc + (obj?.inFare && obj?.airTaxCode != 'FARE_BASE' ? +obj?.inFare : 0);
    }, 0);
  }
  return total.toFixed(2);
};

const initFlight = (type, date, flightInfo) => ({
  type: type || '',
  gate: type === serviceFlightType.outbound ? flightInfo.dest || '' : flightInfo.gate || '',
  gateCode: type === serviceFlightType.outbound ? flightInfo.airportCode || '' : flightInfo.gateCode || '',
  depDate: date || null,
  depTime: null,
  dest: type === serviceFlightType.outbound ? flightInfo.gate || '' : flightInfo.dest || '',
  destCode: type === serviceFlightType.outbound ? flightInfo.gateCode || '' : flightInfo.airportCode || '',
  airline: '',
  airlineCode: '',
  flightNo: '',
  fClass: serviceFlight.map((f) => f.type).toString(),
  arrDate: null,
  arrTime: null,
  via: '',
  adt: 0,
  chd: 0,
  direct: false,
  classesAvailable: {},
  flightNumber: null,
  operated: null,
  stopFlights: []
});

const getFlighstClass = (flights) => {
  classOutbound.value = [];
  classInbound.value = [];
  segmentGroups.value = [];
  flights?.forEach((flight) => {
    if (flight.type === 'Outbound' && classOutbound.value.length <= 4) {
      classOutbound.value.push(flight?.fClass?.trim());
    } else if (flight.type === 'Inbound' && classInbound.value.length <= 4) {
      classInbound.value.push(flight?.fClass?.trim());
    }
    segmentGroups.value.push({
      depDate: formatDate(flight?.depDate, undefined, false),
      depCode: flight?.gateCode,
      destCode: flight?.destCode,
      marketingCompany: flight?.operated,
      flightNumber: +flight?.flightNumber,
      airClass: flight?.fClass?.trim()
    });
  });
  classOutbound.value.join(', ');
  classInbound.value.join(', ');
};

const convertOccNameTable = (rowName: string) => {
  return titleHotel.find((th) => th.key === rowName)?.name;
};

const getTime = (date) => {
  return date ? DateTime.fromISO(new Date(date).toISOString()).toLocal().toFormat('T') : '';
};

const getMarAdjAndTotalMar = (changeData, key, clonePrice) => {
  const tempMarAdj = Array(changeData[key].length);
  const tempTotalMar = Array(changeData[key].length);
  for (let i = 0; i < changeData[key].length; i++) {
    tempMarAdj[i] = ((Number(clonePrice.marAdj[key][i]) || 0) + changeData[key][i]).toFixed(2);
    tempTotalMar[i] = ((Number(clonePrice.totalMar[key][i]) || 0) + changeData[key][i]).toFixed(2);
  }
  return { tempMarAdj: tempMarAdj, tempTotalMar: tempTotalMar };
};

const getSellingPriceAndTotalMar = (changeData, key, clonePrice) => {
  const tempSellingPrice = Array(changeData[key].length);
  const tempTotalMar = Array(changeData[key].length);
  for (let i = 0; i < changeData[key].length; i++) {
    tempSellingPrice[i] = ((Number(clonePrice.sellingPrice[key][i]) || 0) + changeData[key][i]).toFixed(2);
    tempTotalMar[i] = ((Number(clonePrice.totalMar[key][i]) || 0) + changeData[key][i]).toFixed(2);
  }
  return { tempSellingPrice: tempSellingPrice, tempTotalMar: tempTotalMar };
};

const getSellingPrice = (changeData, key, clonePrice) => {
  const temp = Array(changeData[key].length);
  for (let i = 0; i < changeData[key].length; i++) {
    temp[i] = ((Number(clonePrice.sellingPrice[key][i]) || 0) + changeData[key][i]).toFixed(2);
  }
  return temp;
};

const onRowEditSaveHotel = ({ newData, data }, packageData, hotelType, indexHotel, roomIndex, indexPackage) => {
  const { rowCanAutoCalulations, editedRow, changeData } = formatDataBeforeSaveRow(newData, data, oldChildValue.value);
  const clonePrice = cloneDeep(packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price);
  switch (editedRow) {
    case rowCanAutoCalulations.sellingPrice:
      for (const key in changeData) {
        if (key === 'childPrices') {
          const temp = getMarAdjAndTotalMar(changeData, key, clonePrice);
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.marAdj[key] = temp.tempMarAdj;
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.totalMar[key] = temp.tempTotalMar;
        } else {
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.marAdj[key] = (Number(clonePrice.marAdj[key]) || 0) + changeData[key];
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.totalMar[key] = (Number(clonePrice.totalMar[key]) || 0) + changeData[key];
        }
      }
      break;
    case rowCanAutoCalulations.marginAdjustment:
      for (const key in changeData) {
        if (key === 'childPrices') {
          const temp = getSellingPriceAndTotalMar(changeData, key, clonePrice);
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.sellingPrice[key] = temp.tempSellingPrice;
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.totalMar[key] = temp.tempTotalMar;
        } else {
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.sellingPrice[key] = (Number(clonePrice.sellingPrice[key]) || 0) + changeData[key];
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.totalMar[key] = (Number(clonePrice.totalMar[key]) || 0) + changeData[key];
        }
      }
      break;
    case rowCanAutoCalulations.costAdjustment:
      for (const key in changeData) {
        packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price[HOTEL_COST_ADJ] = newData;
        if (key === 'childPrices') {
          const temp = getSellingPrice(changeData, key, clonePrice);
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.sellingPrice[key] = temp;
        } else {
          packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price.sellingPrice[key] = (Number(clonePrice.sellingPrice[key]) || 0) + changeData[key];
        }
      }
      break;
  }
  packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price[newData.rowName] = formatChildPrices(newData);
  worksheetPrices.value[renderStartIndex(packageData.packages, indexPackage, indexHotel) + roomIndex] = filterPriceList(
    packageData.packages[indexPackage][hotelType][indexHotel].rooms[roomIndex].price,
    authorizations?.worksheet_package,
    authorizations?.worksheet_package_additional_info
  );
  handleRenderData(indexPackage);
};

const formatChildPrices = (packageData: any) => {
  if (packageData.childPrices && Array.isArray(packageData.childPrices)) {
    packageData.childPrices = packageData.childPrices.map((price) => {
      if (typeof price === 'number') {
        return price.toFixed(2);
      }
      return price;
    });
  }
  return packageData;
};

const onRowEditSaveFlight = (event, pFlight) => {
  const { index, newData } = event;
  const newGate = newData?.gate;
  const newDest = newData?.dest;

  let depTime = newData?.depTime?.toString();
  if (depTime?.length > 5) {
    depTime = getTime(depTime);
  }

  let arrTime = newData?.arrTime?.toString();
  if (arrTime?.length > 5) {
    arrTime = getTime(arrTime);
  }

  if (typeof newGate === 'object') {
    newData.gateCode = newGate.airportCode;
    newData.gate = `${newGate.airportNameLabel} (${newGate.airportCode})`;
    newData.gateFr = `${newGate.airportNameLabelFr} (${newGate.airportCode})`;
  }
  if (typeof newDest === 'object') {
    newData.destCode = newDest.airportCode;
    newData.dest = `${newDest.airportNameLabel} (${newDest.airportCode})`;
    newData.destFr = `${newDest.airportNameLabelFr} (${newDest.airportCode})`;
  }

  if (isCheckValidateDate(newData, pFlight)) {
    newData.isNew = false;

    pFlight[index] = { ...newData, depTime, arrTime };
    pFlight[index].airline = airlineList.value?.find((air) => air.code === pFlight[index].operated)?.name;
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.validate_date') });
  }
  getFlighstClass(pFlight);
};

const handleSaveComment = (data) => {
  data.comment = editComment.value.data;
  editComment.value = {
    index: null,
    data: ''
  };
};

const handleSaveCommentHotelPrefer = (data) => {
  data.comment = editCommentHotelPrefer.value.data;
  editCommentHotelPrefer.value = {
    index: null,
    data: ''
  };
};

const handleSaveAdditionalHotel = (data) => {
  data.additionalHotelDetail = editAdditionalHotel.value.data;
  editAdditionalHotel.value = {
    index: null,
    data: ''
  };
};

const handleSaveIntro = (packageData) => {
  packageData.intro = editIntro.value.data;
  editIntro.value = {
    isEdit: false,
    data: ''
  };
};

const handleSaveTermCondition = (packageData) => {
  packageData.termConditions = editTermConditions.value.data;
  editTermConditions.value = {
    isEdit: false,
    data: ''
  };
};

const handleData = (indexPackage: number, indexHotel: number, indexRoom: number) => {
  const indexValue = renderStartIndex(packageWorkSheet.value.packages, indexPackage, indexHotel) + indexRoom;
  const additionalTax = {
    ardTax: {},
    netMargin: {},
    priceBreak: []
  };
  worksheetPrices.value[indexValue]?.forEach((tax: any) => {
    if (tax) {
      if (tax.rowName == 'ardTaxes') {
        additionalTax.ardTax = { ...tax, childPrices: tax.childPrices ? [...tax.childPrices] : [] };
        delete additionalTax.ardTax.rowName;
        delete additionalTax.ardTax.canEdit;
      }
      if (tax.rowName == 'netMargin') {
        additionalTax.netMargin = { ...tax, childPrices: tax.childPrices ? [...tax.childPrices] : [] };
        delete additionalTax.netMargin.rowName;
        delete additionalTax.netMargin.canEdit;
      }
    }
  });
  additionalTax.priceBreak = getTaxes?.value[indexValue];
  additionalTax.vendor = flightVendor?.value[indexValue];

  return additionalTax;
};

const renderDataHotel = (data: any, indexPackage: number) => {
  const hotelTemp: any = [];
  data.forEach((hotel: any, indexHotel: number) => {
    const hotelData = {
      comment: hotel.comment,
      additionalHotelDetail: hotel.additionalHotelDetail,
      hCode: hotel.hCode,
      code: hotel.code,
      hotel: hotel.hotel,
      promotion: hotel.promotion,
      additionalNote: hotel.additionalNote,
      rooms: []
    };

    const hotelRooms = hotel.rooms.map((room, indexRoom: number) => {
      for (const property in room.price) {
        delete room.price[property].key;
        delete room.price[property].rowName;
        if (replaceNullsWithZero(room.price[property].childPrices)) {
          room.price[property].childPrices = replaceNullsWithZero(room.price[property].childPrices);
        }
      }

      const additionalTax = handleData(indexPackage, indexHotel, indexRoom);
      return {
        price: room.price,
        code: room.code,
        checkinDate: room.checkinDate,
        checkoutDate: room.checkoutDate,
        hotelDuration: room.hotelDuration,
        promotion: room.promotion,
        comment: room.comment,
        room: room.room,
        roomFr: room.roomFr,
        longName: room.longName,
        longNameFr: room.longNameFr,
        totalTax: room.totalTax,
        additionalTax: additionalTax,
        maxPaxAdult: room.maxPaxAdult,
        maxPaxChild: room.maxPaxChild,
        maxPaxRoom: room.maxPaxRoom,
        minAdPerChild: room.minAdPerChild,
        minPaxAdult: room.minPaxAdult,
        minPaxChild: room.minPaxChild,
        minPaxRoom: room.minPaxRoom,
        mandatoryInfo: room.mandatoryInfo,
        rateOfExchange: room.rateOfExchange,
        currency: room.currency,
        roomInfo: room.roomInfo,
        detailedOccupancy: room.detailedOccupancy,
        adultTransferCost: room.adultTransferCost,
        childTransferCost: room.childTransferCost
      };
    });
    hotelData.rooms = hotelRooms;
    hotelTemp.push(hotelData);
  });
  return hotelTemp;
};

const replaceNullsWithZero = (data) => {
  return data ? data.map((price) => (price === null ? '0.00' : price)) : null;
};

const renderFlightNo = (operated: any, flightNumber: any) => {
  return operated && flightNumber ? `${operated}${flightNumber}` : '';
};

const getCurrencyAndRateExchange = () => {
  if (newDataPricing.roomData.length) {
    priceDetail.value.currency = newDataPricing.roomData[0].currency;
    priceDetail.value.rateOfExchange = newDataPricing.roomData[0].rateOfExchange;
  }
};

const onSave = async () => {
  const submitData = cloneDeep(packageWorkSheet.value.packages);
  const packageTemp: any = [];
  submitData.forEach((pkg, indexPackage: number) => {
    pkg.flights?.forEach((fly) => {
      fly.flightNo = renderFlightNo(fly?.operated, fly?.flightNumber);

      ['arrDate', 'depDate']?.forEach((key) => {
        fly[key] = formatDate(fly[key], 'yyyy-LL-dd HH:mm').replace(' ', 'T');
        delete fly.index;
      });
    });
    const pkgCurrency = priceDetail.value.currency ? priceDetail.value.currency : pkg.currency;
    const pkgRateOfExchange = priceDetail.value.rateOfExchange ? priceDetail.value.rateOfExchange : pkg.rateOfExchange;
    const packageForm = {
      currency: pkgCurrency,
      rateOfExchange: pkgRateOfExchange,
      airportCode: pkg.airportCode,
      adtNo: pkg.adtNo,
      chdNo: pkg.chdNo,
      comment: pkg.comment,
      promotion: pkg.promotion,
      depDate: pkg.depDate,
      dest: pkg.dest,
      destFr: pkg.destFr,
      destCode: pkg.destCode,
      direct: pkg.direct,
      duration: pkg.duration,
      flights: pkg.flights,
      numOfRooms: pkg.numOfRooms,
      gate: pkg.gate,
      gateFr: pkg.gateFr,
      gateCode: pkg.gateCode,
      hotels: renderDataHotel(pkg.hotels, indexPackage),
      hotelPrefer: renderDataHotel(pkg.hotelPrefer, indexPackage),
      infNo: pkg.infNo,
      pnr: pkg.pnr,
      price: pkg.price,
      retDate: pkg.retDate,
      stops: pkg.stops,
      transfers: pkg.transfers,
      additionalNote: pkg.additionalNote,
      brochure: pkg.brochure,
      netMargin: doublePricePublic.value[indexPackage]
    };
    packageTemp.push(packageForm);
  });

  const payload = {
    qType: packageWorkSheet.value.qType,
    bookedDate: new Date(formatDate(packageWorkSheet.value?.bookedDate)),
    expires: new Date(formatDate(packageWorkSheet.value?.expires)),
    intro: handleConvertBrTag(packageWorkSheet.value.intro),
    termConditions: handleConvertBrTag(packageWorkSheet.value.termConditions),
    packages: packageTemp,
    linkedQuotes: packageWorkSheet.value.linkedQuotes
  };
  submitWorksheetStore
    .handleUpdateWorkSheet(payload, packageWorkSheet.value.sk)
    .then(() => {
      disablePreviewPDF.value = false;
      reloadList();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const handleDuplicateFlight = () => {
  packageWorkSheet.value?.packages.forEach((pkg) => {
    pkg.flights.forEach((fly, indexF) => {
      fly.index = indexF;
    });
  });
};

const handleSavePromo = (data) => {
  data.promotion = promoEditedData.value.data;
  promoEditedData.value = {
    index: null,
    data: ''
  };
};

const handleSavePromoHotelPrefer = (data) => {
  data.promotion = editPromoHotelPrefer.value.data;
  editPromoHotelPrefer.value = {
    index: null,
    data: ''
  };
};

const getNewPricing = async (body, indexHotel, indexPackage, packageWorkSheetData, hotelType) => {
  newDataPricing.packageIndex = indexPackage;
  newDataPricing.hotelIndex = indexHotel;
  newDataPricing.hotelType = hotelType;
  isLoading.value = true;
  packageStore
    .getPricingQuote(packageWorkSheetData.sk, body)
    .then((res) => {
      if (res) {
        newPrice.value = true;
        const cloneHotel = cloneDeep(packageWorkSheetData?.packages[indexPackage]?.[hotelType][indexHotel]);
        newDataPricing.roomData = res.data?.hotelPricing;
        newDataPricing.hotelData = cloneHotel;
        newDataPricing.flightData = res.data?.flightInfo;
      }
    })
    .catch((error) => {
      if (error.response?.status === 404) {
        errorMessage.value = error.response?.data.detail;
      }
      newPrice.value = false;
      newDataPricing.roomData = '';
      newDataPricing.hotelData = '';
      newDataPricing.flightData = '';
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const getNewPromotion = (body, indexHotel, indexPackage, packageWorkSheetData, hotelType) => {
  newDataPricing.packageIndex = indexPackage;
  newDataPricing.hotelIndex = indexHotel;
  newDataPricing.hotelType = hotelType;
  isLoading.value = true;
  packageStore
    .getHcmsPromotion(packageWorkSheetData.sk, body)
    .then((res) => {
      if (res) {
        newHcms.value = true;
        isEmptyRespond.value = !res.data;
        newDataPricing.promotions = res.data?.promotion;
        newDataPricing.additionalHotelDetail = res.data?.additionalHotelDetail;
      }
    })
    .catch(() => {
      newHcms.value = false;
      newDataPricing.promotions = '';
      newDataPricing.additionalHotelDetail = '';
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleCloseAndApplyPromotion = async (packageWorkSheetData: any) => {
  packageWorkSheetData.packages[newDataPricing.packageIndex][newDataPricing.hotelType][newDataPricing.hotelIndex].promotion = newDataPricing.promotions;
  packageWorkSheetData.packages[newDataPricing.packageIndex][newDataPricing.hotelType][newDataPricing.hotelIndex].additionalHotelDetail = newDataPricing.additionalHotelDetail;
  visible.value = false;
  onSave();
};

const onSaveAll = (hotel, packageWorkSheetData) => {
  if (newDataPricing.flightData?.length > 0) {
    packageWorkSheetData.packages[newDataPricing.packageIndex].flights = formatFlightData(newDataPricing.flightData);
    getFlighstClass(packageWorkSheetData.packages[newDataPricing.packageIndex].flights);
  }
  packageWorkSheetData.packages[newDataPricing.packageIndex].hotels[newDataPricing.hotelIndex].rooms = hotel.rooms;
  formatData(packageWorkSheetData);
  onSave();
  handleRenderData(+newDataPricing.packageIndex);
  visible.value = false;
};

const formatData = (packageWorkSheetData: any) => {
  worksheetPrices.value = [];
  numberDynamicColumns.value = [];
  packageWorkSheetData.packages.forEach((p: any) => {
    p.hotels.forEach((h: any) => {
      h.rooms.forEach((room: any) => {
        const listPrice = filterPriceList(room.price, authorizations?.worksheet_package, authorizations?.worksheet_package_additional_info);
        worksheetPrices.value.push(listPrice);
        numberDynamicColumns.value.push(listPrice?.[0]?.childPrices?.length);
      });
    });
  });
};

const previewNewPrice = (newData: any, packageWorkSheetData: any) => {
  priceDetail.value.currency = '';
  priceDetail.value.rateOfExchange = 1;
  const oldRoomsValue = packageWorkSheetData?.packages[newData.packageIndex]?.hotels[newData.hotelIndex]?.rooms;
  const dialogRef = dialog.open(ViewPricingModal, {
    data: {
      hotelData: newData.hotelData,
      roomData: newData.roomData,
      flightData: newData.flightData,
      oldRoomsValue,
      permissions: authorizations?.worksheet_package
    },
    props: {
      header: t('common.preview'),
      maximizable: true,
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    },
    emits: {
      onSaveSelectRoom: (hotel) => {
        getCurrencyAndRateExchange();
        onSaveAll(hotel, packageWorkSheetData);
        removeCostCalculationData();
        dialogRef.close();
      }
    }
  });
};

const previewNewPromotion = (newData) => {
  dialog.open(ViewPricingModal, {
    data: {
      promotions: newData.promotions,
      additionalHotelDetail: newData.additionalHotelDetail,
      permissions: authorizations?.worksheet_package
    },
    props: {
      header: t('common.preview'),
      maximizable: true,
      style: {
        width: '90vw'
      },
      modal: true
    }
  });
};

const handleOpenModal = (indexHotel, indexPackage, packageWorkSheetData, hotelType: string, promotion?: string) => {
  const roomCodes = [];
  packageWorkSheetData?.packages[indexPackage]?.[hotelType][indexHotel]?.rooms.forEach((item) => {
    roomCodes.push(item.code);
  });

  const body = {
    depDate: packageWorkSheetData?.packages[indexPackage]?.depDate,
    retDate: packageWorkSheetData?.packages[indexPackage]?.retDate,
    gateCode: packageWorkSheetData?.packages[indexPackage]?.gateCode,
    airportCode: packageWorkSheetData?.packages[indexPackage]?.airportCode,
    hCode: packageWorkSheetData?.packages[indexPackage]?.[hotelType][indexHotel].hCode,
    numOfRooms: packageWorkSheetData?.packages[indexPackage]?.numOfRooms,
    adtNo: packageWorkSheetData?.packages[indexPackage]?.adtNo,
    chdNo: packageWorkSheetData?.packages[indexPackage]?.chdNo,
    lang: packageWorkSheetData?.lang,
    option: `${+indexPackage + 1}`,
    brochure: packageWorkSheetData?.packages[indexPackage]?.brochure,
    roomCode: roomCodes.join('|')
  };
  if (promotion) {
    getNewPromotion(body, indexHotel, indexPackage, packageWorkSheetData, hotelType);
    isPromotionModal.value = true;
  } else {
    getNewPricing(body, indexHotel, indexPackage, packageWorkSheetData, hotelType);
    isPromotionModal.value = false;
  }
  visible.value = true;
};

const getHotelRoom = (hotel) => {
  let result = '';
  hotel.rooms.forEach((room, indexR) => {
    if (indexR > 0) {
      result += ',\n';
    }
    result += `${getTextVariant(lang, room.longName, room.longNameFr)} (${room.code})`;
  });
  return result;
};

const getHotel = (pkg: any) => {
  let result = '';
  pkg.hotels.forEach((hotel: any, indexH: any) => {
    if (indexH > 0) {
      result += ',\n';
    }
    result += `${hotel.hotel} (${hotel.hCode})`;
  });
  return result;
};

const getRoom = (pkg: any) => {
  let result = '';
  pkg.hotels.forEach((hotel: any) => {
    const roomTexts = hotel.rooms.map((room: any) => `${getTextVariant(lang, room.room, room.roomFr)} (${room.code})`);
    result += roomTexts.join(', ') + '\n';
  });
  return result;
};

const getHotelDuration = (pkg: any) => {
  const durationsSet = new Set<number>();

  pkg.hotels.forEach((hotel: any) => {
    hotel?.rooms?.forEach((room: any) => {
      const duration = room?.hotelDuration;
      if (typeof duration === 'number') {
        durationsSet.add(duration);
      }
    });
  });

  return Array.from(durationsSet).join(', ');
};

const getCheckInOutDateRoom = (pkg, key = 'checkinDate') => {
  return formatDate(pkg.hotels[0].rooms[0][key], 'dd-LLL-yyyy', false, lang);
};

const getListAirline = async () => {
  const response = await adminQuoteStore.getAirlines();
  if (response) {
    airlineList.value = response;
  }
};
getListAirline();

const addNewFlight = (packageData, type) => {
  const groupFlights = packageData.flights.filter((flight) => flight.type === type);
  const numberOfFlight = groupFlights?.length;
  if (numberOfFlight < 4) {
    let lastIndex = -1;
    packageData.flights?.forEach((flight, index) => {
      if (flight.type === type) {
        lastIndex = index;
      }
    });
    const initFlightKey = `flight${Date.now()}${packageData.flights.length}`;
    packageData.flights.splice(lastIndex + 1, 0, initFlightData(type, initFlightKey));
    editingFlightRows.value = packageData.flights.filter((flight) => editingFlightRows.value.find((row) => row.key === flight.key) || flight.key === initFlightKey);
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.maximum_flight_notification') });
  }
};

const deleteFlight = (packageData, flight) => {
  packageData.flights = packageData.flights.filter((f) => f.key !== flight.key);
};

const searchPackageGateway = (event) => {
  if (!event.query.trim().length || event.originalEvent.type !== 'input') {
    filterGateway.value = [...airportList.value.slice(0, 100)];
  } else {
    filterGateway.value = airportList.value.filter((air) => {
      return onFilterAirport(air, event);
    });
  }
};

const searchPackageDestination = (event) => {
  if (!event.query.trim().length || event.originalEvent.type !== 'input') {
    filterDestination.value = [...airportList.value.slice(0, 100)];
  } else {
    filterDestination.value = airportList.value.filter((air) => {
      return onFilterAirport(air, event);
    });
  }
};

const handleSaveExpiry = (date) => {
  packageWorkSheet.value.expires = date;
  editExpiryDate.value.isEdit = false;
};

const handleSaveDateBooked = (date) => {
  packageWorkSheet.value.bookedDate = date;
  editDateBooked.value.isEdit = false;
};

const handleSaveAdult = (adtNo, p) => {
  p.adtNo = adtNo;
  editAdult.value.isEdit = false;
};

const handleSaveChild = (chdNo, p) => {
  p.chdNo = chdNo;
  editChild.value.isEdit = false;
};

const handleSaveNumberOfRoom = (numOfRooms, p) => {
  p.numOfRooms = numOfRooms;
  editNumberOfRoom.value.isEdit = false;
};

const handleSaveHotelCategory = () => {
  loadingWorksheetPackage.value = true;
  adminQuoteStore
    .getQuoteDetail(packageWorkSheet.value?.sk)
    .then(() => {
      handleRenderData();
    })
    .finally(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      editHotelRoomCategoryPackage.value.index = null;
      loadingWorksheetPackage.value = false;
    });
};

const handleCancleHotelCategory = (listRoom: string) => {
  editHotelRoomCategoryPackage.value.index = '';
  editHotelRoomCategoryPackage.value.data = listRoom;
};

const changeLinkedQuote = (event: any) => {
  packageWorkSheet.value.linkedQuotes = event;
};

const validateFlightData = (flightsData) => {
  return flightsData.every((flight) => Object.values(flight).every((flightKey) => flightKey !== null && flightKey !== undefined && flightKey !== ''));
};

const handleOpenViewFlightSeat = (packageWorkSheetOption: any, tabIndex: number) => {
  const flightTemp = [];
  packageWorkSheetOption.flights?.forEach((flight) => {
    const flightOption = {
      type: flight.type,
      gateCode: flight.gateCode,
      destCode: flight.destCode,
      depDate: formatDate(flight.depDate, undefined, false),
      retDate: formatDate(flight.arrDate, undefined, false),
      direct: !!flight.direct,
      airlineCode: flight.operated,
      number: flight.flightNumber
    };
    flightTemp.push(flightOption);
  });

  const payload = [
    {
      gateCode: packageWorkSheetOption.gateCode,
      destCode: packageWorkSheetOption.airportCode,
      depDate: formatDate(packageWorkSheetOption.depDate, undefined, false),
      retDate: formatDate(packageWorkSheetOption.retDate, undefined, false),
      direct: !!packageWorkSheetOption.direct,
      flightRequest: flightTemp,
      option: `${+tabIndex + 1}`
    }
  ];

  isLoadingButton.value = true;
  const validateFlightPayload = validateFlightData(flightTemp);
  if (validateFlightPayload) {
    airManagementStore
      .getFlightSeat(payload)
      .then((res) => {
        dialog.open(ViewFlightSeat, {
          data: {
            flightInfo: packageWorkSheetOption.flights,
            flightSeatInfo: res
          },
          props: {
            header: t('worksheet.flight_details'),
            maximizable: true,
            style: {
              width: '95vw'
            },
            breakpoints: {
              '960px': '75vw',
              '640px': '90vw'
            },
            modal: true
          }
        });
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
      })
      .finally(() => {
        isLoadingButton.value = false;
      });
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.please_fill_field') });
    isLoadingButton.value = false;
  }
};

const getMarAdjAndSellingPriceAndTotalMar = (valueChange, key, cloneData, changeData) => {
  const tempMarAdj = Array(valueChange[key].length).fill(0);
  const tempSellingPrice = Array(valueChange[key].length).fill(0);
  const tempTotalMar = Array(valueChange[key].length).fill(0);
  for (let i = 0; i < valueChange[key].length; i++) {
    if (cloneData && cloneData.sellingPrice && cloneData.sellingPrice[key] && changeData && cloneData.sellingPrice[key][i]) {
      tempMarAdj[i] = Number(cloneData.sellingPrice[key][i]) ? Number(cloneData.marAdj[key][i]) + changeData[key][i] : Number(cloneData.marAdj[key][i]);
      tempSellingPrice[i] = Number(cloneData.sellingPrice[key][i]) ? Number(cloneData.sellingPrice[key][i]) + changeData[key][i] : Number(cloneData.sellingPrice[key][i]);
      tempTotalMar[i] = Number(cloneData.sellingPrice[key][i]) ? Number(cloneData.totalMar[key][i]) + changeData[key][i] : Number(cloneData.totalMar[key][i]);
    }
  }
  return { tempMarAdj: tempMarAdj, tempSellingPrice: tempSellingPrice, tempTotalMar: tempTotalMar };
};

const onSaveAdjustOptionMargin = (packageData: any, data: any, indexPackage: number, indexHotel: number) => {
  const valueChange: any = {
    childOnePrice: data.childMargin,
    childTwoPrice: data.childMargin,
    childThreePrice: data.childMargin,
    doublePrice: data.adultMargin,
    quadPrice: data.adultMargin,
    singlePrice: data.adultMargin,
    triplePrice: data.adultMargin,
    fiveInRoomPrice: data.adultMargin,
    sixInRoomPrice: data.adultMargin
  };

  data.hotelRoomCate.forEach((roomCatIndex: number) => {
    valueChange.childPrices = Array(numberDynamicColumns.value[renderStartIndex(packageWorkSheet.value.packages, indexPackage, indexHotel) + roomCatIndex]).fill(data.childMargin);
    const cloneData: any = cloneDeep(packageData.hotels[indexHotel].rooms[roomCatIndex].price);
    const { changeData } = formatDataBeforeSaveRow(valueChange, cloneData.marAdj);

    for (const key in valueChange) {
      if (key === 'childPrices') {
        const temp = getMarAdjAndSellingPriceAndTotalMar(valueChange, key, cloneData, changeData);
        packageData.hotels[indexHotel].rooms[roomCatIndex].price.marAdj[key] = temp.tempMarAdj;
        packageData.hotels[indexHotel].rooms[roomCatIndex].price.sellingPrice[key] = temp.tempSellingPrice;
        packageData.hotels[indexHotel].rooms[roomCatIndex].price.totalMar[key] = temp.tempTotalMar;
      } else {
        packageData.hotels[indexHotel].rooms[roomCatIndex].price.marAdj[key] = Number(cloneData.sellingPrice[key]) ? Number(cloneData.marAdj[key]) + changeData[key] : Number(cloneData.marAdj[key]);
        packageData.hotels[indexHotel].rooms[roomCatIndex].price.sellingPrice[key] = Number(cloneData.sellingPrice[key]) ? Number(cloneData.sellingPrice[key]) + changeData[key] : Number(cloneData.sellingPrice[key]);
        packageData.hotels[indexHotel].rooms[roomCatIndex].price.totalMar[key] = Number(cloneData.sellingPrice[key]) ? Number(cloneData.totalMar[key]) + changeData[key] : Number(cloneData.totalMar[key]);
      }
    }

    worksheetPrices.value[renderStartIndex(packageWorkSheet.value.packages, indexPackage, indexHotel) + roomCatIndex] = filterPriceList(
      packageData.hotels[indexHotel].rooms[roomCatIndex].price,
      authorizations?.worksheet_package,
      authorizations?.worksheet_package_additional_info
    );
  });
};

const onSaveCostAdjustmentTool = (packageData: any, data: any, indexPackage: number, indexHotel: number) => {
  const worksheetID = packageWorkSheet.value.sk;
  const optionNo = indexPackage;
  packageStore
    .saveQuoteAdjusmentInfo(worksheetID, optionNo, roomAdjustmentInfo.value)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      data.forEach((room, roomIndex) => {
        const cloneData: any = cloneDeep(packageData.hotels[indexHotel].rooms[roomIndex].price);

        const { changeData } = formatDataBeforeSaveRow(room, cloneData.costAdj);
        for (const key in changeData) {
          packageData.hotels[indexHotel].rooms[roomIndex].price[HOTEL_COST_ADJ] = room;
          packageData.hotels[indexHotel].rooms[roomIndex].price.costAdj = room;
          if (key === 'childPrices') {
            const temp = getSellingPrice(changeData, key, cloneData);
            packageData.hotels[indexHotel].rooms[roomIndex].price.sellingPrice[key] = temp;
          } else {
            packageData.hotels[indexHotel].rooms[roomIndex].price.sellingPrice[key] = (parseInt(cloneData.sellingPrice[key], 10) ?? 0) + changeData[key];
          }
        }
        worksheetPrices.value[renderStartIndex(packageWorkSheet.value.packages, indexPackage, indexHotel) + roomIndex] = filterPriceList(
          packageData.hotels[indexHotel].rooms[roomIndex].price,
          authorizations?.worksheet_package,
          authorizations?.worksheet_package_additional_info
        );
      });
    })
    .catch((error) => {
      toast.add({ severity: 'error', summary: '', detail: error.response?.data?.message, life: 5000 });
    });
};

const handleOpenAdjustOptionMarginModal = (packageWorkSheetOption: any, indexPackage: number, indexHotel: number) => {
  dialog.open(AdjustOptionMarginModal, {
    data: {
      roomData: packageWorkSheetOption.hotels[indexHotel].rooms
    },
    props: {
      header: t('worksheet.adjust_option_margin'),
      maximizable: true,
      style: {
        width: '30vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true
    },
    emits: {
      onApply: (data) => {
        onSaveAdjustOptionMargin(packageWorkSheetOption, data, indexPackage, indexHotel);
      }
    }
  });
};

const removeCostCalculationData = () => {
  const worksheetID = packageWorkSheet.value.sk;
  if (packageWorkSheet.value?.packages?.length && currentOption.value <= packageWorkSheet.value?.packages?.length - 1) {
    packageStore.deleteQuoteAdjusmentInfo(worksheetID, currentOption.value).catch((error) => {
      toast.add({ severity: 'error', summary: '', detail: error.response?.data?.message, life: 5000 });
    });
  }
};

const handleOpenCostCalculationModal = (packageWorkSheetOption: any, indexPackage: number, indexHotel: number) => {
  isLoadingTool.value = true;
  const worksheetID = packageWorkSheet.value.sk;
  const optionNo = indexPackage;
  packageStore
    .getQuoteAdjusmentInfo(worksheetID, optionNo)
    .then((res) => {
      roomAdjustmentInfo.value = res.data;
      dialog.open(QuoteAdjustmentToolModal, {
        data: {
          roomData: roomAdjustmentInfo.value
        },
        props: {
          header: t('worksheet.quote_calculation_tool') + ' (' + worksheetID + ')',
          style: { width: '90vw' },
          maximizable: true,
          modal: true
        },
        emits: {
          onApply: (data) => {
            onSaveCostAdjustmentTool(packageWorkSheetOption, data, indexPackage, indexHotel);
          }
        }
      });
    })
    .catch((error) => {
      toast.add({ severity: 'error', summary: '', detail: error.response?.data?.message, life: 5000 });
    })
    .finally(() => {
      isLoadingTool.value = false;
    });
};

const openPriceBreakModal = (indexValue: number) => {
  const dialogRef = dialog.open(PriceBreakdownModal, {
    props: {
      header: t('worksheet.price_breakdown'),
      modal: true
    },
    data: {
      getPostSearch: getTaxes.value[indexValue]
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const checkDateQuote = (indexPackage: number) => {
  const depDateOfQuote = packageWorkSheet.value?.packages[indexPackage].depDate;
  return new Date(depDateOfQuote).getTime() < today.getTime();
};

const getFareSearch = (indexPackage: number, indexHotel: number, roomIndex: number, oldData: any) => {
  visible.value = true;
  isSearchAdditional.value = true;
  if (!checkDateQuote(indexPackage)) {
    const gatewayCode = packageWorkSheet.value?.packages[indexPackage].gateCode;
    const destination = packageWorkSheet.value?.packages[indexPackage].airportCode;
    const duration = packageWorkSheet.value?.packages[indexPackage].duration;
    packageStore
      .getArdTaxSearchAdditional(gatewayCode, destination, duration)
      .then((res: any) => {
        getTaxes346.value = res.data;
        getArdTaxes(indexPackage, indexHotel, roomIndex, oldData);
        getNetMargin(indexPackage, indexHotel, roomIndex, oldData);
      })
      .catch((error) => {
        if (error.response?.status === 400) {
          const errorMessage = error.response?.data?.message == 'Constraint Violation' ? t('notification.process_failed') : error.response?.data?.message;
          toast.add({ severity: 'error', summary: '', detail: errorMessage, life: 5000 });
        }
      })
      .finally(() => {
        visible.value = false;
        isSearchAdditional.value = false;
      });
  } else {
    packageStore
      .postSearchAdditional({
        segmentGroups: segmentGroups.value,
        numberOfAdult: adtNo.value,
        numberOfChild: chdNo.value,
        flightClass: cabins.value,
        flightVendor: flightVendor.value,
        option: `${+indexPackage + 1}`
      })
      .then((res) => {
        if (res) {
          const indexValue = renderStartIndex(packageWorkSheet.value.packages, indexPackage, indexHotel) + roomIndex;
          getTaxes.value[indexValue] = res.data;
          getBaseFare(res.data, indexPackage, indexHotel, roomIndex, oldData);
          getArdTaxes(indexPackage, indexHotel, roomIndex, oldData);
          getNetMargin(indexPackage, indexHotel, roomIndex, oldData);
        }
      })
      .catch((error) => {
        if (error.response?.status === 400) {
          const errorMessage = error.response?.data?.message == 'Constraint Violation' ? t('notification.process_failed') : error.response?.data?.message;
          toast.add({ severity: 'error', summary: '', detail: errorMessage, life: 5000 });
        }
      })
      .finally(() => {
        visible.value = false;
        isSearchAdditional.value = false;
      });
  }
};

const getPriceList = (indexRoom: number) => {
  return worksheetPrices.value[indexRoom];
};

const getChildPrices = (data) => {
  oldChildValue.value = cloneDeep(data.childPrices);
};

const onRowEditCancel = (event: DataTableCellEditCancelEvent, listValue: any) => {
  listValue[event.index].childPrices = oldChildValue.value;
};

const checkConditionTotal = (indexPackage: number, value: any) => {
  return !checkDateQuote(indexPackage) ? getTaxes346.value : value;
};

const checkFirstNumber = (inputString: string) => {
  const openingParenIndex = inputString.indexOf('(');
  const closeParenIndex = inputString.indexOf(')');
  const dashIndex = inputString.indexOf('-');
  if (openingParenIndex !== -1 && dashIndex !== -1 && openingParenIndex === 0) {
    const numberString = inputString.substring(dashIndex + 1, closeParenIndex).trim();
    const firstNumber = +numberString;
    return firstNumber >= 12;
  } else if (openingParenIndex !== -1 && dashIndex !== -1) {
    const numberString = inputString.substring(dashIndex + 1, openingParenIndex).trim();
    const firstNumber = +numberString;
    return firstNumber >= 12;
  } else {
    return false;
  }
};

const renderStartIndex = (packageWorkSheet: any, indexPackage: number, indexHotel?: number) => {
  let finalIndex = 0;

  for (let i = 0; i < indexPackage; i++) {
    const hotels = packageWorkSheet[i]?.hotels || [];
    for (const hotel of hotels) {
      finalIndex += hotel.rooms?.length || 0;
    }
  }

  if (indexHotel) {
    const currentHotels = packageWorkSheet[indexPackage]?.hotels || [];
    for (let j = 0; j < indexHotel; j++) {
      finalIndex += currentHotels[j]?.rooms?.length || 0;
    }
  }
  return finalIndex;
};

const handleCheckPriceInvalid = (sellingPrice: any, agencyTaxesPrice: any) => {
  return isInvalidValuePrice(sellingPrice) && isInvalidValuePrice(agencyTaxesPrice);
};

const handleCheckAdditionalPrice = (additionalPrice: any, sellingPrice: any) => {
  return additionalPrice && sellingPrice;
};

const getBaseFare = (taxes: any, indexPackage: number, indexHotel: number, indexRoom: number, oldData: any) => {
  const objSellPrice = oldData?.find((obj) => obj?.rowName === 'sellingPrice');
  const objAgencyTaxesPrice = oldData?.find((obj) => obj?.rowName === 'taxes');
  const objFilterChild = oldData?.find((obj) => obj?.rowName === 'occupanciesEdit');
  const objTotalSell = oldData?.find((obj) => obj?.rowName === 'priceSummary');
  const objBase = oldData?.find((obj) => obj?.rowName === 'ardBaseFare');
  const indexValue = renderStartIndex(packageWorkSheet.value.packages, indexPackage, indexHotel) + indexRoom;
  if (handleCheckAdditionalPrice(objBase, objSellPrice)) {
    taxes?.forEach((tax) => {
      if (tax?.airTaxCode == 'FARE_BASE') {
        objBase.canEdit = false;
        objBase.rowName = 'ardBaseFare';
        objBase.singlePrice = tax.adtFare;
        objBase.doublePrice = tax.adtFare;
        objBase.triplePrice = tax.adtFare;
        objBase.quadPrice = tax.adtFare;
        objBase.fiveInRoomPrice = tax.adtFare;
        objBase.sixInRoomPrice = tax.adtFare;
        objBase.infantPrice = tax.inFare;
        objBase.childOnePrice = tax.chFare;
        objBase.childTwoPrice = tax.chFare;
        objBase.childThreePrice = tax.chFare;
        objBase.childPrices = Array(numberDynamicColumns.value[indexValue]).fill(tax.chFare);
        const temp = Array(objTotalSell.childPrices.length);
        for (let i = 0; i < objTotalSell.childPrices.length; i++) {
          if (objFilterChild?.childPrices[i] && checkFirstNumber(objFilterChild?.childPrices[i])) {
            temp[i] = tax.adtFare;
          } else {
            temp[i] = tax.chFare;
          }
        }
        for (let i = 0; i < objSellPrice.childPrices.length; i++) {
          const sellingChildPriceVal = objSellPrice?.childPrices?.[i];
          const agencyTaxesChildPriceVal = objAgencyTaxesPrice?.childPrices?.[i];

          if (handleCheckPriceInvalid(sellingChildPriceVal, agencyTaxesChildPriceVal)) {
            temp[i] = '0.00';
          }
        }
        objBase.childPrices = temp;
      }
    });
    Object.keys(objSellPrice).forEach((key) => {
      const sellingPriceVal = objSellPrice[key];
      const agencyTaxesPriceVal = objAgencyTaxesPrice?.[key];

      if (handleCheckPriceInvalid(sellingPriceVal, agencyTaxesPriceVal)) {
        objBase[key] = '0.00';
      }
    });
  }
  worksheetPrices.value[indexValue] = oldData;
  listPriceFilter.value = oldData;
  return listPriceFilter;
};

const getArdTaxes = (indexPackage: number, indexHotel: number, indexRoom: number, oldData: any) => {
  const objSellPrice = oldData?.find((obj) => obj?.rowName === 'sellingPrice');
  const objAgencyTaxesPrice = oldData?.find((obj) => obj?.rowName === 'taxes');
  const objFilterChild = oldData?.find((obj) => obj?.rowName === 'occupanciesEdit');
  const objTotalSell = oldData?.find((obj) => obj?.rowName === 'priceSummary');
  const objTaxes = oldData?.find((obj) => obj?.rowName === 'ardTaxes');
  const indexValue = renderStartIndex(packageWorkSheet.value.packages, indexPackage, indexHotel) + indexRoom;
  const totalAdt = checkConditionTotal(indexPackage, getTotalAdtFare(indexValue));
  const totalCh = checkConditionTotal(indexPackage, getTotalChFare(indexValue));
  const totalInf = checkConditionTotal(indexPackage, getTotalInFare(indexValue));
  if (handleCheckAdditionalPrice(objTaxes, objSellPrice)) {
    objTaxes.canEdit = false;
    objTaxes.rowName = 'ardTaxes';
    objTaxes.singlePrice = totalAdt;
    objTaxes.doublePrice = totalAdt;
    objTaxes.triplePrice = totalAdt;
    objTaxes.quadPrice = totalAdt;
    objTaxes.fiveInRoomPrice = totalAdt;
    objTaxes.sixInRoomPrice = totalAdt;
    objTaxes.infantPrice = totalInf;
    objTaxes.childOnePrice = totalCh;
    objTaxes.childTwoPrice = totalCh;
    objTaxes.childThreePrice = totalCh;
    objTaxes.childPrices = Array(numberDynamicColumns.value[indexValue]).fill(totalCh);
    const temp = Array(objTotalSell.childPrices.length);
    for (let i = 0; i < objTotalSell.childPrices.length; i++) {
      if (objFilterChild?.childPrices[i] && checkFirstNumber(objFilterChild?.childPrices[i])) {
        temp[i] = totalAdt;
      } else {
        temp[i] = totalCh;
      }
    }
    for (let i = 0; i < objSellPrice.childPrices.length; i++) {
      const sellingChildPriceVal = objSellPrice?.childPrices?.[i];
      const agencyTaxesChildPriceVal = objAgencyTaxesPrice?.childPrices?.[i];

      if (handleCheckPriceInvalid(sellingChildPriceVal, agencyTaxesChildPriceVal)) {
        temp[i] = '0.00';
      }
    }
    objTaxes.childPrices = temp;
    Object.keys(objSellPrice).forEach((key) => {
      const sellingPriceVal = objSellPrice[key];
      const agencyTaxesPriceVal = objAgencyTaxesPrice?.[key];

      if (handleCheckPriceInvalid(sellingPriceVal, agencyTaxesPriceVal)) {
        objTaxes[key] = '0.00';
      }
    });
  }
  worksheetPrices.value[indexValue] = oldData;
  listPriceFilter.value = oldData;
  return listPriceFilter;
};

const getNetMargin = (indexPackage: number, indexHotel: number, indexRoom: number, oldData: any) => {
  const objSellPrice = oldData?.find((obj) => obj?.rowName === 'sellingPrice');
  const objAgencyTaxesPrice = oldData?.find((obj) => obj?.rowName === 'taxes');
  const objFilterChild = oldData?.find((obj) => obj?.rowName === 'occupanciesEdit');
  const objNet = oldData?.find((obj) => obj?.rowName === 'netMargin');
  const indexValue = renderStartIndex(packageWorkSheet.value.packages, indexPackage, indexHotel) + indexRoom;
  const totalAdt = checkConditionTotal(indexPackage, getTotalAdtFare(indexValue));
  const totalCh = checkConditionTotal(indexPackage, getTotalChFare(indexValue));
  const totalInf = checkConditionTotal(indexPackage, getTotalInFare(indexValue));
  const objTotalSell = oldData?.find((obj) => obj?.rowName === 'priceSummary');
  const objTotalCost = oldData?.find((obj) => obj?.rowName === 'costSummary');
  if (handleCheckAdditionalPrice(objNet, objSellPrice)) {
    objNet.canEdit = false;
    objNet.rowName = 'netMargin';
    objNet.singlePrice = (+objTotalSell?.singlePrice - +objTotalCost?.singlePrice - +totalAdt).toFixed(2);
    objNet.doublePrice = (+objTotalSell?.doublePrice - +objTotalCost?.doublePrice - +totalAdt).toFixed(2);
    objNet.triplePrice = (+objTotalSell?.triplePrice - +objTotalCost?.triplePrice - +totalAdt).toFixed(2);
    objNet.quadPrice = (+objTotalSell?.quadPrice - +objTotalCost?.quadPrice - +totalAdt).toFixed(2);
    objNet.fiveInRoomPrice = (+objTotalSell?.fiveInRoomPrice - +objTotalCost?.fiveInRoomPrice - +totalAdt).toFixed(2);
    objNet.sixInRoomPrice = (+objTotalSell?.sixInRoomPrice - +objTotalCost?.sixInRoomPrice - +totalAdt).toFixed(2);
    objNet.infantPrice = (+objTotalSell?.infantPrice - +objTotalCost?.infantPrice - +totalInf).toFixed(2);
    objNet.childOnePrice = (+objTotalSell?.childOnePrice - +objTotalCost?.childOnePrice - +totalCh).toFixed(2);
    objNet.childTwoPrice = (+objTotalSell?.childTwoPrice - +objTotalCost?.childTwoPrice - +totalCh).toFixed(2);
    objNet.childThreePrice = (+objTotalSell?.childThreePrice - +objTotalCost?.childThreePrice - +totalCh).toFixed(2);
    const temp = Array(objTotalSell.childPrices.length);
    for (let i = 0; i < objTotalSell.childPrices.length; i++) {
      if (objFilterChild?.childPrices[i] && checkFirstNumber(objFilterChild?.childPrices[i])) {
        temp[i] = (+objTotalSell?.childPrices[i] - +objTotalCost?.childPrices[i] - +totalAdt).toFixed(2);
      } else {
        temp[i] = (+objTotalSell?.childPrices[i] - +objTotalCost?.childPrices[i] - +totalCh).toFixed(2);
      }
    }
    for (let i = 0; i < objSellPrice.childPrices.length; i++) {
      const sellingChildPriceVal = objSellPrice?.childPrices?.[i];
      const agencyTaxesChildPriceVal = objAgencyTaxesPrice?.childPrices?.[i];

      if (handleCheckPriceInvalid(sellingChildPriceVal, agencyTaxesChildPriceVal)) {
        temp[i] = '0.00';
      }
    }
    objNet.childPrices = temp;
    Object.keys(objSellPrice).forEach((key) => {
      const sellingPriceVal = objSellPrice[key];
      const agencyTaxesPriceVal = objAgencyTaxesPrice?.[key];

      if (handleCheckPriceInvalid(sellingPriceVal, agencyTaxesPriceVal)) {
        objNet[key] = '0.00';
      }
    });
    if (indexRoom === 0) {
      doublePricePublic.value[indexPackage] = objNet.doublePrice;
    }
  }
  worksheetPrices.value[indexValue] = oldData;
  listPriceFilter.value = oldData;
  return listPriceFilter;
};

const convertTaxes346ToNumber = (data: any) => {
  let prices = [data?.singlePrice, data?.doublePrice, data?.triplePrice, data?.quadPrice, data?.fiveInRoomPrice, data?.sixInRoomPrice, data?.infantPrice, data?.childOnePrice, data?.childTwoPrice, data?.childThreePrice];
  prices = prices.map((price) => parseFloat(price)).filter((price) => price !== 0.0);
  const uniquePrices = [...new Set(prices)];
  return uniquePrices[0];
};

const initData = (num: number = 0) => {
  adtNo.value = packageWorkSheet.value?.packages[num]?.adtNo;
  chdNo.value = packageWorkSheet.value?.packages[num]?.chdNo;
  packageData.depDate = packageWorkSheet.value?.packages[num]?.depDate;
  packageData.duration = packageWorkSheet.value?.packages[num]?.duration;
  getFlighstClass(packageWorkSheet.value?.packages[num]?.flights);
  currentOption.value = num;
};

const handleRenderData = (editPackageIndex: number = 0) => {
  packageWorkSheet.value?.packages?.forEach((pkg, packageIndex) => {
    editingRowsHotel.value[packageIndex] = {};
    editingRowsHotelPrefer.value[packageIndex] = {};
    pkg.hotels.forEach((hotel, indexH) => {
      editingRowsHotel.value[packageIndex][indexH] = [];
      hotel.rooms.forEach((room, index) => {
        getTaxes.value.push(room?.additionalTax?.priceBreak ?? []);
        getTaxes346.value = convertTaxes346ToNumber(room?.additionalTax?.ardTax) || 0;
        flightVendor.value = room?.additionalTax?.vendor && flightVendorList.includes(room?.additionalTax?.vendor) ? room?.additionalTax?.vendor : 'VAC';
        room.price.ardBaseFare = ardBaseFareObj;
        room.price.ardTaxes = ardTaxesObj;
        room.price.netMargin = netMarginObj;
        const listPrice = filterPriceList(room.price, authorizations?.worksheet_package, authorizations?.worksheet_package_additional_info);
        listPriceFilter.value = listPrice;
        worksheetPrices.value.push(listPrice);
        numberDynamicColumns.value.push(listPrice?.[0]?.childPrices?.length);
        if (getTaxes.value) {
          getBaseFare(room?.additionalTax?.priceBreak, packageIndex, indexH, index, listPriceFilter.value);
          getArdTaxes(packageIndex, indexH, index, listPriceFilter.value);
          getNetMargin(packageIndex, indexH, index, listPriceFilter.value);
        }
      });
    });
    pkg.hotelPrefer.forEach((_hotel, indexH) => {
      editingRowsHotelPrefer.value[packageIndex][indexH] = [];
    });
    if (!pkg.flights || (pkg.flights && !pkg.flights.length)) {
      pkg.flights = [initFlight(serviceFlightType.outbound, pkg.depDate, pkg), initFlight(serviceFlightType.inbound, pkg.retDate, pkg)];
    }

    pkg.flights = formatFlightData(pkg.flights);
    adtNo.value = pkg.adtNo;
    chdNo.value = pkg.chdNo;
    segmentGroups.value = [];
    pkg.flights.forEach((flight) => {
      segmentGroups.value.push({
        depDate: formatDate(flight.depDate, undefined, false),
        depCode: flight.gateCode,
        destCode: flight.destCode,
        marketingCompany: flight.operated,
        flightNumber: +flight.flightNumber,
        airClass: flight?.fClass?.trim()
      });
    });
    const arrayCabin = [...new Set(pkg.flights.map((item) => item.cabin))];
    cabins.value = arrayCabin.join(', ');
  });
  handleDuplicateFlight();
  initData(editPackageIndex);
  editExpiryDate.value.data = packageWorkSheet.value?.expires && new Date(packageWorkSheet.value?.expires);
  editDateBooked.value.data = packageWorkSheet.value?.bookedDate && new Date(packageWorkSheet.value?.bookedDate);
};

const handleChildPackageInput = (value: any, field: string, indexColumn: number) => {
  isShowError.value = !handleChildInput(value, field, indexColumn, validationInputs);
};

const handleCancleChildInput = (editorCancelCallback: any) => {
  editorCancelCallback();
  isShowError.value = false;
};

const handleSaveChildInput = (editorSaveCallback: any, data: any) => {
  if (data.rowName !== 'occupanciesEdit') {
    isShowError.value = false;
    editorSaveCallback();
  } else {
    checkEditChildInput(editorSaveCallback, isShowError.value);
  }
};

onBeforeMount(() => {
  handleRenderData();
});
</script>
<template>
  <LoadingSpinner v-if="loadingWorksheetPackage" />
  <div class="package-worksheet">
    <section id="header" class="my-3 px-3">
      <div class="grid">
        <div class="col-6 text-lg font-bold">{{ $t('worksheet.groups_quote') }}</div>
        <div class="col-6 text-right text-lg font-bold">({{ packageWorkSheet?.sk.includes('IQ') ? $t('worksheet.package_instaquote') : $t('worksheet.package_manual_quote') }})</div>
      </div>
    </section>
    <section id="information" class="px-3">
      <div class="grid">
        <div class="col-3">
          <div class="quote-id font-bold">
            <div class="grid">
              <div class="col-6 mb-4">{{ $t('worksheet.quote_id') }}:</div>
              <div class="col-6 font-normal">{{ packageWorkSheet?.sk }}</div>
            </div>
          </div>
          <LinkedQuotesEditor :worksheetData="packageWorkSheet" :permission="hasUserPermission(packageWorkSheet, canEditHeader)" @changeLinkedQuote="changeLinkedQuote($event)"></LinkedQuotesEditor>
        </div>
        <div class="col-4">
          <div class="date-quote font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.date_quoted') }}:</div>
              <div class="col-8 font-normal">{{ formatDate(packageWorkSheet?.created, 'dd-LLL-yyyy', true, lang) }}</div>
            </div>
          </div>
          <div class="expiry-date font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.expiry_date') }}:</div>
              <div class="col-8 font-normal">
                <div v-if="!editExpiryDate.isEdit" class="flex align-items-top">
                  <div class="min-w-5rem">
                    {{ formatDate(packageWorkSheet?.expires, 'dd-LLL-yyyy', false, lang) }}
                  </div>
                  <span class="text-center ml-4">
                    <i v-if="hasUserPermission(packageWorkSheet, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editExpiryDate.isEdit = true"></i>
                  </span>
                </div>
                <div v-else class="flex align-items-top">
                  <div class="w-9rem">
                    <DatePicker id="calendar-24h" v-model="editExpiryDate.data" dateFormat="dd-M-yy">
                      <template #footer>
                        <p class="text-center">
                          <small>{{ $t('common.press_space') }}</small>
                        </p>
                      </template>
                    </DatePicker>
                  </div>
                  <div class="text-center ml-4 flex gap-3 align-items-center">
                    <i class="pi pi-check cursor-pointer" v-if="editExpiryDate.data" @click="handleSaveExpiry(editExpiryDate.data)"></i>
                    <i class="pi pi-times cursor-pointer" @click="editExpiryDate = { isEdit: false, data: packageWorkSheet?.expires && new Date(packageWorkSheet?.expires) }"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="book-date font-bold">
            <div class="grid">
              <div class="col-4">{{ $t('worksheet.book_dated') }}:</div>
              <div class="col-8 font-normal">
                <div v-if="!editDateBooked.isEdit" class="flex align-items-top">
                  <div class="min-w-5rem">
                    {{ formatDate(packageWorkSheet?.bookedDate, 'dd-LLL-yyyy', false, lang) }}
                  </div>
                  <span class="text-center ml-4">
                    <i v-if="hasUserPermission(packageWorkSheet, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editDateBooked.isEdit = true"></i>
                  </span>
                </div>
                <div v-else class="flex align-items-top">
                  <div class="w-9rem">
                    <DatePicker dateFormat="dd-M-yy" id="calendar-24h" v-model="editDateBooked.data">
                      <template #footer>
                        <p class="text-center">
                          <small>{{ $t('common.press_space') }}</small>
                        </p>
                      </template>
                    </DatePicker>
                  </div>
                  <div class="text-center ml-4 flex gap-3 align-items-center">
                    <i class="pi pi-check cursor-pointer" @click="handleSaveDateBooked(editDateBooked.data)"></i>
                    <i class="pi pi-times cursor-pointer" @click="editDateBooked = { isEdit: false, data: packageWorkSheet?.bookedDate && new Date(packageWorkSheet?.bookedDate) }"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-5">
          <div class="quote-assigned-to font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.quote_assigned_to') }}:</div>
              <div class="col-8 font-normal">{{ packageWorkSheet?.assignedTo === UNASSIGNED ? $t('status.unassigned') : packageWorkSheet?.assignedTo }}</div>
            </div>
          </div>

          <div class="book-by font-bold">
            <div class="grid">
              <div class="col-4">{{ $t('worksheet.book_by') }}:</div>
              <div class="col-8 font-normal flex gap-1">
                <div>{{ packageWorkSheet?.user }}</div>
                <div>{{ packageWorkSheet?.email && `(${packageWorkSheet?.email})` }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section id="details">
      <Tabs :value="0" @update:value="initData($event)">
        <TabList>
          <Tab v-for="(_, index) in packageWorkSheet?.packages" :key="index" :value="index">{{ 'OPTION ' + Number(index + 1) }}</Tab>
          <Tab v-if="hasUserPermission(packageWorkSheet, canViewTermConditions)" :key="packageWorkSheet?.packages?.length" :value="packageWorkSheet?.packages?.length">{{ $t('menu.term_and_condition') }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel v-for="(p, index) in packageWorkSheet?.packages" :key="index" :value="index">
            <div v-if="authorizations.quote_management.net_margin_view" class="average-net-margin">
              <b>{{ `${$t('worksheet.average_net_margin')}:` }}</b>
              <span>{{ doublePricePublic[index] }}</span>
            </div>
            <div class="option">
              <div class="lead-option">{{ $t('common.package') }}</div>
              <!-- Row 1 -->
              <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap w-full mt-2 mb-2">
                <div class="flex-1">
                  <div class="flex gap-4">
                    <div class="min-w-label--new font-bold">{{ $t('common.gateway') }}:</div>
                    <div class="font-normal">{{ getTextVariant(lang, p.gate, p.gateFr) }} ({{ p.gateCode }})</div>
                  </div>
                </div>
                <div class="flex-1">
                  <div class="flex gap-4">
                    <div class="min-w-label--new font-bold">{{ $t('worksheet.hotel') }}:</div>
                    <div class="font-normal white-space-pre-line">{{ getHotel(p) }}</div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="font-bold">
                    <div class="flex justify-content-end">
                      <div class="w-14px mr-4"></div>
                      <div class="min-w-label">{{ $t('worksheet.brochure') }}:</div>
                      <div class="font-normal w-6rem">{{ p.brochure }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Row 2 -->
              <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap w-full row-2">
                <div class="flex-1 mt-2 mb-2">
                  <div class="flex gap-4">
                    <div class="min-w-label--new font-bold">{{ $t('common.destination') }}:</div>
                    <div class="font-normal">{{ getTextVariant(lang, p.dest, p.destFr) }} ({{ p.airportCode }})</div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="flex gap-4">
                    <div class="font-bold" :class="lang === 'en' ? 'min-w-label--new' : 'max-w-label'">{{ $t('worksheet.hotel_room_category') }}:</div>
                    <div class="font-normal white-space-pre-line">{{ getRoom(p) }}</div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="adults font-bold">
                    <div class="flex justify-content-end" v-if="!editAdult.isEdit">
                      <span class="text-center mr-4">
                        <i v-if="hasUserPermission(packageWorkSheet, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editAdult = { isEdit: true, data: p.adtNo }"></i>
                      </span>
                      <div class="min-w-label">{{ $t('common.adults') }}:</div>
                      <div class="font-normal w-6rem">{{ p.adtNo }}</div>
                    </div>
                    <div class="flex justify-content-end" v-else>
                      <div class="min-w-label--new">{{ $t('common.adults') }}:</div>
                      <div class="w-3rem">
                        <InputNumber v-model="editAdult.data" :min="0" />
                      </div>
                      <div class="text-center ml-2 flex gap-2 align-items-center">
                        <i class="pi pi-check cursor-pointer" @click="handleSaveAdult(editAdult.data, p)"></i>
                        <i class="pi pi-times cursor-pointer" @click="editAdult = { isEdit: false, data: p.adtNo }"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Row 3 -->
              <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap w-full">
                <div class="flex-1 mt-2 mb-2">
                  <div class="flex gap-4">
                    <div class="min-w-label--new font-bold">{{ $t('worksheet.departure_date') }}:</div>
                    <div class="font-normal">{{ formatDate(p.depDate, 'dd-LLL-yyyy', false, lang) }}</div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="flex gap-4">
                    <div class="min-w-label--new font-bold">{{ $t('worksheet.check_in_date') }}:</div>
                    <div class="font-normal">{{ getCheckInOutDateRoom(p) }}</div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="child font-bold">
                    <div class="flex justify-content-end" v-if="!editChild.isEdit">
                      <span class="text-center mr-4">
                        <i v-if="hasUserPermission(packageWorkSheet, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editChild = { isEdit: true, data: p.chdNo }"></i>
                      </span>
                      <div class="min-w-label">{{ $t('common.children') }}:</div>
                      <div class="font-normal w-6rem">{{ p.chdNo }}</div>
                    </div>
                    <div class="flex justify-content-end" v-else>
                      <div class="min-w-label">{{ $t('common.children') }}:</div>
                      <div class="w-3rem">
                        <InputNumber v-model="editChild.data" :min="0" />
                      </div>
                      <div class="text-center ml-2 flex gap-2 align-items-center">
                        <i class="pi pi-check cursor-pointer" @click="handleSaveChild(editChild.data, p)"></i>
                        <i class="pi pi-times cursor-pointer" @click="editChild = { isEdit: false, data: p.chdNo }"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Row 4 -->
              <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap w-full">
                <div class="flex-1 mt-2 mb-2">
                  <div class="flex gap-4">
                    <div class="min-w-label--new font-bold">{{ $t('worksheet.return_date_package') }}:</div>
                    <div class="font-normal">{{ formatDate(p.retDate, 'dd-LLL-yyyy', false, lang) }}</div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="flex gap-4">
                    <div class="min-w-label--new font-bold">{{ $t('worksheet.check_out_date') }}:</div>
                    <div class="font-normal">{{ getCheckInOutDateRoom(p, 'checkoutDate') }}</div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="total font-bold">
                    <div class="flex justify-content-end">
                      <div class="w-14px mr-4"></div>
                      <div class="min-w-label">{{ $t('worksheet.total_passengers') }}:</div>
                      <div class="font-normal w-6rem">{{ p.adtNo + p.chdNo }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Row 5 -->
              <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap w-full">
                <div class="flex-1 mt-2 mb-2">
                  <div class="duration font-bold">
                    <div class="flex gap-4">
                      <div class="min-w-label--new">{{ $t('worksheet.flight_duration') }}:</div>
                      <div class="font-normal w-6rem">{{ p?.duration }}</div>
                    </div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="duration font-bold">
                    <div class="flex gap-4">
                      <div class="min-w-label--new">{{ $t('worksheet.hotel_duration') }}:</div>
                      <div class="font-normal w-6rem">{{ getHotelDuration(p) }}</div>
                    </div>
                  </div>
                </div>
                <div class="flex-1 mt-2 mb-2">
                  <div class="number_room font-bold">
                    <div class="flex justify-content-end" v-if="!editNumberOfRoom.isEdit">
                      <span class="text-center mr-4">
                        <i v-if="hasUserPermission(packageWorkSheet, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editNumberOfRoom = { isEdit: true, data: p.numOfRooms }"></i>
                      </span>
                      <div class="min-w-label">{{ $t('advance_quote.form.number_of_room') }}:</div>
                      <div class="font-normal w-6rem">{{ p.numOfRooms }}</div>
                    </div>
                    <div class="flex" v-else>
                      <div class="min-w-label">{{ $t('advance_quote.form.number_of_room') }}:</div>
                      <div class="w-3rem">
                        <InputNumber v-model="editNumberOfRoom.data" :min="0" />
                      </div>
                      <div class="text-center ml-2 flex gap-2 align-items-center">
                        <i class="pi pi-check cursor-pointer" @click="handleSaveNumberOfRoom(editNumberOfRoom.data, p)"></i>
                        <i class="pi pi-times cursor-pointer" @click="editNumberOfRoom = { isEdit: false, data: p.numOfRooms }"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Row 6 -->
              <div class="detail-option w-full">
                <div class="departure-date font-bold mb-2 mt-2">
                  <div class="flex gap-4 align-items-center">
                    <div class="min-w-label--new">{{ $t('advance_quote.form.additional_note') }}:</div>
                    <div class="font-normal w-full border-1 border-solid border-black p-3">{{ p.additionalNote }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="pricing flight-detail" v-if="hasUserPermission(packageWorkSheet, canViewFlight)">
              <div class="lead-flight flex align-items-center justify-content-between">
                {{ $t('common.flight_details') }}
                <Button size="small" severity="danger" class="view-availability-button" :label="$t('common.buttons.view_availability')" @click="handleOpenViewFlightSeat(p, index)" :loading="isLoadingButton" />
              </div>
              <div class="detail">
                <DataTable v-model:editingRows="editingFlightRows" :value="p.flights" rowGroupMode="subheader" groupRowsBy="groupBy" class="w-full" showGridlines editMode="row" @row-edit-save="onRowEditSaveFlight($event, p.flights)">
                  <template #groupheader="slotProps">
                    <div class="flex align-items-center justify-content-end gap-2">
                      <div class="add-flight-button" @click="addNewFlight(p, slotProps.data.type)">+ {{ $t(`worksheet.add_${slotProps.data.type.toLowerCase()}`) }}</div>
                    </div>
                  </template>
                  <TableColumn field="groupBy"></TableColumn>
                  <TableColumn field="type" :header="$t('worksheet.type')" dataType="string" class="min-w-5rem">
                    <template #body="{ data }">
                      {{ $t(`worksheet.${data.type.toLowerCase()}`) }}
                    </template>
                  </TableColumn>
                  <TableColumn field="operated" :header="$t('worksheet.airline')" dataType="string" class="min-w-9rem">
                    <template #body="{ data }">
                      {{ data.airline }}
                    </template>

                    <template #editor="{ data, field }">
                      <Select v-model="data[field]" :options="airlineList" optionLabel="name" optionValue="code" :placeholder="$t('worksheet.select_type')" class="w-full" />
                    </template>
                  </TableColumn>
                  <TableColumn field="flightNumber" :header="$t('worksheet.flight_number')" dataType="string" class="min-w-5rem">
                    <template #body="{ data }">
                      {{ data.flightNumber }}
                    </template>

                    <template #editor="{ data, field }">
                      <InputText v-model="data[field]" :maxlength="5" @input="validateFlightNumber(data, field)" />
                    </template>
                  </TableColumn>
                  <TableColumn field="gate" :header="$t('worksheet.from')" dataType="string" class="min-w-10rem">
                    <template #body="{ data }">
                      {{ getTextVariant(lang, data.gate, data.gateFr) }}
                    </template>
                    <template #editor="{ data, field }">
                      <AutoComplete v-model="data[field]" :suggestions="filterGateway" @complete="searchPackageGateway($event)" completeOnFocus optionLabel="selectedLabel" class="w-full" forceSelection>
                        <template #option="slotProps">
                          <div class="flex align-options-center">
                            <div>
                              <div>{{ slotProps.option.airportName }} ({{ slotProps.option.airportCode }})</div>
                              <div class="text-sm">{{ slotProps.option.cityName }}, {{ slotProps.option.province || slotProps.option.region }}, {{ slotProps.option.countryName }}</div>
                            </div>
                          </div>
                        </template>
                      </AutoComplete>
                    </template>
                  </TableColumn>
                  <TableColumn field="dest" :header="$t('worksheet.to')" dataType="string" class="min-w-10rem">
                    <template #body="{ data }">
                      {{ getTextVariant(lang, data.dest, data.destFr) }}
                    </template>

                    <template #editor="{ data, field }">
                      <AutoComplete v-model="data[field]" :suggestions="filterDestination" @complete="searchPackageDestination($event)" completeOnFocus optionLabel="selectedLabel" class="w-full" forceSelection>
                        <template #option="slotProps">
                          <div class="flex align-options-center">
                            <div>
                              <div>{{ slotProps.option.airportName }} ({{ slotProps.option.airportCode }})</div>
                              <div class="text-sm">{{ slotProps.option.cityName }}, {{ slotProps.option.province || slotProps.option.region }}, {{ slotProps.option.countryName }}</div>
                            </div>
                          </div>
                        </template>
                      </AutoComplete>
                    </template>
                  </TableColumn>
                  <TableColumn field="depDate" :header="$t('worksheet.dep_date')" class="min-w-10rem">
                    <template #body="{ data }">
                      {{ formatDate(data?.depDate, 'dd-LLL-yyyy', false, lang) }}
                    </template>
                    <template #editor="{ data, field }">
                      <DatePicker id="calendar-24h" v-model="data[field]" dateFormat="dd-M-yy">
                        <template #footer>
                          <p class="text-center">
                            <small>{{ $t('common.press_space') }}</small>
                          </p>
                        </template>
                      </DatePicker>
                    </template>
                  </TableColumn>
                  <TableColumn field="depTime" :header="$t('worksheet.dep_time')" dataType="date" class="min-w-8rem">
                    <template #body="{ data }">
                      {{ data.depTime }}
                    </template>
                    <template #editor="{ data, field }">
                      <DatePicker id="calendar-24h" v-model="data[field]" showTime hourFormat="24" timeOnly>
                        <template #footer>
                          <p class="text-center">
                            <small>{{ $t('common.press_space') }}</small>
                          </p>
                        </template>
                      </DatePicker>
                    </template>
                  </TableColumn>
                  <TableColumn field="arrDate" :header="$t('worksheet.arrival_date')" class="min-w-10rem">
                    <template #body="{ data }">
                      {{ formatDate(data?.arrDate, 'dd-LLL-yyyy', false, lang) }}
                    </template>
                    <template #editor="{ data, field }">
                      <DatePicker id="calendar-24h" v-model="data[field]" dateFormat="dd-M-yy">
                        <template #footer>
                          <p class="text-center">
                            <small>{{ $t('common.press_space') }}</small>
                          </p>
                        </template>
                      </DatePicker>
                    </template>
                  </TableColumn>
                  <TableColumn field="arrTime" :header="$t('worksheet.arrival_time')" dataType="date" class="min-w-8rem">
                    <template #body="{ data }">
                      {{ data.arrTime }}
                    </template>

                    <template #editor="{ data, field }">
                      <DatePicker id="calendar-24h" v-model="data[field]" showTime hourFormat="24" timeOnly>
                        <template #footer>
                          <p class="text-center">
                            <small>{{ $t('common.press_space') }}</small>
                          </p>
                        </template>
                      </DatePicker>
                    </template>
                  </TableColumn>
                  <TableColumn field="via" :header="$t('worksheet.via')" dataType="date" class="w-5rem min-w-5rem">
                    <template #body="{ data }">
                      {{ data.via }}
                    </template>

                    <template #editor="{ data, field }">
                      <InputText v-model="data[field]" :maxlength="3" />
                    </template>
                  </TableColumn>
                  <TableColumn field="fClass" :header="$t('common.class')" dataType="date" class="w-5rem">
                    <template #body="{ data }">
                      <span>{{ data.fClass }}</span>
                    </template>
                    <template #editor="{ data, field }">
                      <InputText v-model="data[field]" :maxlength="5" />
                    </template>
                  </TableColumn>
                  <TableColumn field="cabin" :header="$t('common.cabin')" dataType="string" class="word-break w-10rem max-w-10rem">
                    <template #body="{ data, field }">
                      {{ getCabinName(data[field]) }}
                    </template>

                    <template #editor="{ data, field }">
                      <Select v-model="data[field]" :options="LIST_CABIN" optionLabel="name" optionValue="data" :placeholder="$t('worksheet.select_type')" class="w-full" />
                    </template>
                  </TableColumn>
                  <TableColumn v-if="hasUserPermission(packageWorkSheet, canEditFlight)" :rowEditor="true" class="text-center">
                    <template #body="{ editorInitCallback, data }">
                      <div class="flex flex-column">
                        <i class="pi pi-pencil table-action-button" @click="editorInitCallback()"></i>
                        <i v-if="data.canDelete" class="pi pi-times-circle table-action-button text-primary" @click="deleteFlight(p, data)"></i>
                      </div>
                    </template>
                    <template #editor="{ editorSaveCallback, editorCancelCallback }">
                      <div class="flex flex-column">
                        <i class="pi pi-check table-action-button" @click="editorSaveCallback()"></i>
                        <i class="pi pi-times table-action-button" @click="editorCancelCallback()"></i>
                      </div>
                    </template>
                  </TableColumn>
                </DataTable>
              </div>
            </div>
            <div v-for="(hotel, indexHotel) in p.hotels" :key="indexHotel" class="mt-4">
              <div class="option">
                <div class="lead-option">{{ $t('worksheet.hotel') }}</div>
                <div class="detail-option flex justify-content-between flex-wrap" :class="canAdjustOptionMargin ? 'pr-0' : 'pr-4'">
                  <div>
                    <div class="gateway font-bold mt-2 mb-2">
                      <div class="flex gap-3">
                        <div class="min-w-label">{{ $t('common.gateway') }}:</div>
                        <div class="font-normal">{{ p.gateCode }} ({{ getTextVariant(lang, p.gate, p.gateFr) }})</div>
                      </div>
                    </div>
                    <div class="destination font-bold mb-2">
                      <div class="flex gap-3">
                        <div class="min-w-label">{{ $t('common.destination') }}:</div>
                        <div class="font-normal">{{ getTextVariant(lang, p.dest, p.destFr) }}</div>
                      </div>
                    </div>
                    <div class="hotel font-bold mb-2">
                      <div class="flex gap-3">
                        <div class="min-w-label">{{ $t('worksheet.hotel') }}:</div>
                        <div class="font-normal">{{ hotel.hotel }} ({{ hotel.hCode }})</div>
                      </div>
                    </div>
                    <div class="hotel-room-cat font-bold mb-2">
                      <div class="flex gap-3" v-if="editHotelRoomCategoryPackage.index !== `hotel${index}${indexHotel}`">
                        <div class="min-w-label">{{ $t('worksheet.hotel_room_category') }}:</div>
                        <div class="font-normal max-w-label-room white-space-pre-line">{{ getHotelRoom(hotel) }}</div>
                        <div class="text-center" v-if="canEditRoh && isIncentiveGroupType">
                          <i class="pi pi-pencil cursor-pointer" @click="editHotelRoomCategoryPackage = { index: `hotel${index}${indexHotel}`, data: getHotelRoom(hotel) }"></i>
                        </div>
                      </div>
                      <div v-else>
                        <RunOfHouseSelection
                          :quoteId="packageWorkSheet?.sk"
                          :hotelIndex="indexHotel"
                          :optionIndex="index"
                          :defaultValue="getHotelRoom(hotel)"
                          @saveCategory="handleSaveHotelCategory()"
                          @cancelEditCategory="handleCancleHotelCategory(getHotelRoom(hotel))"
                        ></RunOfHouseSelection>
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-column">
                    <Button data-testid="adjust-option-margin" class="h-3rem mt-2 uppercase font-medium action-button" v-if="canAdjustOptionMargin" @click="handleOpenAdjustOptionMarginModal(p, index, indexHotel)">{{
                      $t('worksheet.adjust_option_margin')
                    }}</Button>
                    <Button
                      v-if="authorizations.quote_management.cost_calculation_view"
                      data-testid="cost-calculation-tool"
                      class="h-3rem mt-2 uppercase font-medium flex justify-content-center action-button"
                      @click="handleOpenCostCalculationModal(p, index, indexHotel)"
                      :loading="isLoadingTool"
                      >{{ $t('worksheet.cost_calculation_tool') }}</Button
                    >
                  </div>
                </div>
              </div>
              <div v-for="(room, roomIndex) in hotel.rooms" :key="roomIndex">
                <div class="pricing">
                  <div class="lead-pricing flex align-items-center justify-content-between">
                    {{ $t('common.pricing') }} ({{ getTextVariant(lang, room.longName, room.longNameFr) }} - {{ room.code }})
                    <Button size="small" severity="danger" :class="roomIndex == 0 ? 'custom-button' : 'hidden'" :label="$t('common.buttons.process')" @click="handleOpenModal(indexHotel, index, packageWorkSheet, 'hotels')" />
                  </div>
                  <div class="pricing-detail">
                    <DataTable
                      rowGroupMode="subheader"
                      v-model:editingRows="editingRowsHotel[index][indexHotel][roomIndex]"
                      :value="getPriceList(renderStartIndex(packageWorkSheet.packages, index, indexHotel) + roomIndex)"
                      class="w-full"
                      editMode="row"
                      data-key="rowName"
                      showGridlines
                      @row-edit-save="onRowEditSaveHotel($event, packageWorkSheet, 'hotels', indexHotel, roomIndex, index)"
                      @row-edit-cancel="onRowEditCancel($event, getPriceList(renderStartIndex(packageWorkSheet.packages, index, indexHotel) + roomIndex))"
                    >
                      <TableColumn header="" dataType="string" class="min-w-10rem h-3rem relative">
                        <template #body="{ data }">
                          <div :class="[data.key === TOTAL_COST ? 'p-3 absolute text-left top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'text-900 font-bold']">
                            <span
                              >{{ convertOccNameTable(data.rowName)
                              }}<i
                                v-if="data.rowName === 'ardTaxes'"
                                class="pi pi-plus-circle ml-1 cursor-pointer"
                                :class="{ disabled: !checkDateQuote(index) }"
                                @click="openPriceBreakModal(renderStartIndex(packageWorkSheet.packages, index, indexHotel) + roomIndex)"
                              ></i
                            ></span>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="singlePrice" :header="$t('book_form.single')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end pr-2 flight-vendor">
                              <span class="uppercase">{{ $t('worksheet.outbound') }}</span>
                            </div>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="doublePrice" :header="$t('book_form.double')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'hidden'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'" class="h-full align-items-center flex justify-content-end pr-2">
                              <InputText v-model="classOutbound" class="w-10 pl-3" disabled />
                            </div>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="triplePrice" :header="$t('book_form.triple')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end pr-2 flight-vendor">
                              <span class="uppercase">{{ $t('worksheet.return') }}</span>
                            </div>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="quadPrice" :header="$t('book_form.quad')" dataType="number" class="relative w-7rem">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'hidden'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'" class="h-full align-items-center flex justify-content-end pr-2">
                              <InputText v-model="classInbound" class="w-10 pl-3" disabled />
                            </div>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="fiveInRoomPrice" :header="$t('book_form.5_in_room')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end pr-3 flight-vendor flight-vendor-lg">
                              <span>VAC</span>
                              <div class="pl-2"><RadioButton v-model="flightVendor" name="flightVendor" value="VAC" /></div>
                            </div>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="sixInRoomPrice" :header="$t('book_form.6_in_room')" dataType="number" class="relative w-7rem">
                        <template #body="{ data, field, index }">
                          <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end px-2 flight-vendor flight-vendor-lg">
                              <span class="uppercase">{{ $t('worksheet.groupsb') }}</span>
                              <div class="pl-2"><RadioButton v-model="flightVendor" name="flightVendor" value="GROUPSB" /></div>
                            </div>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn
                        field="childPrices"
                        :header="$t('common.child')"
                        class="min-w-8rem relative parent-column"
                        v-for="(column, indexColumn) in numberDynamicColumns[renderStartIndex(packageWorkSheet.packages, index, indexHotel) + roomIndex]"
                        :key="'childColumn' + indexColumn"
                      >
                        <template #body="{ data, field, index }">
                          <div v-if="index === 0 || true" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header ']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">
                              {{ data.rowName && data.rowName === 'occupanciesEdit' ? data[field]?.[indexColumn] : Number(data[field]?.[indexColumn]) ? Number(data[field]?.[indexColumn]).toFixed(2) : '0.00' }}
                            </span>
                            <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end px-2 flight-vendor flight-vendor-lg">
                              <span class="uppercase" v-if="indexColumn === 0">{{ $t('worksheet.published') }}</span>
                              <div class="pl-2" v-if="indexColumn === 0"><RadioButton v-model="flightVendor" value="PUBLISHED" name="flightVendor" /></div>
                            </div>
                          </div>
                          <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header ']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field][indexColumn]" class="w-full" />
                          <InputText v-else v-model="data[field][indexColumn]" @input="handleChildPackageInput(data[field][indexColumn], field, indexColumn)" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn field="infantPrice" :header="$t('common.infant')" dataType="number" class="relative">
                        <template #body="{ data, field, index }">
                          <div v-if="index === 0">
                            {{ data[field] }}
                          </div>
                          <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                            <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                            <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'" class="flex justify-content-end px-2">
                              <span class="uppercase" v-if="numberDynamicColumns.length === 0 || !numberDynamicColumns">{{ $t('worksheet.published') }}</span>
                              <div class="pl-2" v-if="numberDynamicColumns.length === 0 || !numberDynamicColumns"><RadioButton v-model="flightVendor" value="PUBLISHED" name="flightVendor" /></div>
                            </div>
                          </div>
                        </template>
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                          <InputText v-else v-model="data[field]" class="w-full" />
                        </template>
                      </TableColumn>
                      <TableColumn :rowEditor="true" class="text-center relative w-5rem" :header="$t('common.action')">
                        <template #body="{ editorInitCallback, data }">
                          <div :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header right-0']">
                            <span v-if="data.key !== TOTAL_COST && data.key !== PRICE_KEY.HEADER && data.canEdit" @click="getChildPrices(data)"><i class="pi pi-pencil" @click="editorInitCallback"></i></span>
                          </div>
                          <p v-if="data.key === TOTAL_COST || data.key === PRICE_KEY.HEADER" class="opacity-0" :class="{ hidden: data.rowName === 'additionInformation' }">{{ $t('common.edit') }}</p>
                          <div v-if="authorizations?.worksheet_package_additional_info?.process_button && data.rowName == 'additionInformation'">
                            <Button
                              id="fare-search-button"
                              class="action-button"
                              size="small"
                              severity="danger"
                              :label="$t('common.buttons.process')"
                              @click="getFareSearch(index, indexHotel, roomIndex, worksheetPrices[renderStartIndex(packageWorkSheet.packages, index, indexHotel) + roomIndex])"
                            />
                          </div>
                        </template>
                        <template #editor="{ data, editorSaveCallback, editorCancelCallback }">
                          <div class="flex">
                            <i class="pi pi-check edit-pi" @click="handleSaveChildInput(editorSaveCallback, data)"></i>
                            <i class="pi pi-times edit-pi" @click="handleCancleChildInput(editorCancelCallback)"></i>
                          </div>
                        </template>
                      </TableColumn>
                    </DataTable>
                  </div>
                </div>
              </div>
              <div class="promotions" v-if="hasUserPermission(packageWorkSheet, canViewPromotion)">
                <div class="lead-promotions flex align-items-center justify-content-between">
                  {{ $t('worksheet.promotions') }}
                  <Button size="small" class="action-button" severity="danger" :label="$t('common.buttons.process')" @click="handleOpenModal(indexHotel, index, packageWorkSheet, 'hotels', 'promotion')" />
                </div>
                <div v-if="promoEditedData.index !== `hotel${index}${indexHotel}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotel.promotion" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(packageWorkSheet, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoEditedData = { index: `hotel${index}${indexHotel}`, data: hotel.promotion }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="promoEditedData.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(hotel)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="promoEditedData.index = ''"></i>
                  </div>
                </div>
              </div>
              <div class="additional-hotel">
                <div class="lead-additional-hotel">{{ $t('worksheet.additional_hotel_detail') }}</div>
                <div v-if="editAdditionalHotel.index !== `hotel${index}${indexHotel}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotel.additionalHotelDetail" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i class="pi pi-pencil edit-pi cursor-pointer" @click="editAdditionalHotel = { index: `hotel${index}${indexHotel}`, data: hotel.additionalHotelDetail }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editAdditionalHotel.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveAdditionalHotel(hotel)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editAdditionalHotel.index = ''"></i>
                  </div>
                </div>
              </div>
              <div class="internal-comment" v-if="hasUserPermission(packageWorkSheet, canViewAgencyComment)">
                <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                <div v-if="editComment.index !== `hotel${index}${indexHotel}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotel.comment" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(packageWorkSheet, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `hotel${index}${indexHotel}`, data: hotel.comment }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editComment.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveComment(hotel)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = ''"></i>
                  </div>
                </div>
              </div>
            </div>
            <div v-for="(hotelPrefer, indexHotelPrefer) in p.hotelPrefer" :key="indexHotelPrefer" class="mt-6 hotel-prefer">
              <div class="title-hotel-prefer">
                <div class="title-hotel font-bold">{{ $t('worksheet.title_hotel_prefer') + ' - ' + hotelPrefer.hotel }}</div>
              </div>
              <div class="hotel-detail mt-3">
                <div class="title-hotel font-bold mb-3 text-900">{{ $t('worksheet.hotel_detail') }}</div>
                <div class="flex gap-6 flex-wrap md:flex-nowrap">
                  <div>
                    <img :src="p.hotelPreferPdf[indexHotelPrefer]?.imgUrl || fallBackImage" alt="" class="h-15rem w-22rem" />
                  </div>
                  <div class="min-w-20rem">
                    <div class="mb-1 font-bold text-lg text-900">{{ p.hotelPreferPdf[indexHotelPrefer]?.hotel }}</div>
                    <div class="mb-1">
                      <i v-for="(item, index) in parseInt(p.hotelPreferPdf[indexHotelPrefer]?.overallRate / 2)" :key="index" class="pi mr-1 pi-star-fill text-2xl text-color-secondary"></i>
                      <i v-if="+p.hotelPreferPdf[indexHotelPrefer]?.overallRate % 2 > 0 || true" class="text-color-secondary relative top-2px">
                        <img :src="semistarImage" alt="star" />
                      </i>
                    </div>
                    <p class="text-sm text-900">{{ p.hotelPreferPdf[indexHotelPrefer]?.description }}</p>
                    <div class="text-900 text-sm">
                      <a href="javascript:" class="text-lg" @click="handleRedirectLink($event, p.hotelPreferPdf[indexHotelPrefer]?.urlDetail)">{{ $t('worksheet.hotel_detail') }}</a>
                      ({{ $t('worksheet.hint') }})
                    </div>
                  </div>
                </div>
              </div>
              <div class="promotions" v-if="hasUserPermission(packageWorkSheet, canViewPromotion)">
                <div class="lead-promotions flex align-items-center justify-content-between">
                  {{ $t('worksheet.promotions') }}
                  <Button size="small" class="process-button" severity="danger" :label="$t('common.buttons.process')" @click="handleOpenModal(indexHotelPrefer, index, packageWorkSheet, 'hotelPrefer', 'promotion')" />
                </div>
                <div v-if="editPromoHotelPrefer?.index !== `hotel${indexHotelPrefer}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotelPrefer.promotion" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(packageWorkSheet, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="editPromoHotelPrefer = { index: `hotel${indexHotelPrefer}`, data: hotelPrefer.promotion }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editPromoHotelPrefer.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromoHotelPrefer(hotelPrefer)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editPromoHotelPrefer.index = ''"></i>
                  </div>
                </div>
              </div>
              <div class="internal-comment" v-if="hasUserPermission(packageWorkSheet, canViewAgencyComment)">
                <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                <div v-if="editCommentHotelPrefer.index !== `hotel${indexHotelPrefer}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="hotelPrefer.comment" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(packageWorkSheet, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editCommentHotelPrefer = { index: `hotel${indexHotelPrefer}`, data: hotelPrefer.comment }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editCommentHotelPrefer.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveCommentHotelPrefer(hotelPrefer)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editCommentHotelPrefer.index = ''"></i>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel v-if="hasUserPermission(packageWorkSheet, canViewTermConditions)" :value="packageWorkSheet?.packages?.length">
            <div class="term_of_c">
              <div class="intro">
                <h4 class="font-bold text-lg">{{ $t('common.intro') }}</h4>
                <div v-if="!editIntro.isEdit" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="packageWorkSheet.intro" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(packageWorkSheet, canEditTermConditions)" class="pi pi-pencil edit-pi cursor-pointer" @click="editIntro = { isEdit: true, data: packageWorkSheet.intro }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editIntro.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveIntro(packageWorkSheet)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editIntro.isEdit = false"></i>
                  </div>
                </div>
              </div>
              <div class="term_conditions mt-4">
                <h4 class="font-bold text-lg">{{ $t('menu.term_and_condition') }}</h4>
                <div v-if="!editTermConditions.isEdit" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="packageWorkSheet.termConditions" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(packageWorkSheet, canEditTermConditions)" class="pi pi-pencil edit-pi cursor-pointer" @click="editTermConditions = { isEdit: true, data: packageWorkSheet.termConditions }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editTermConditions.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveTermCondition(packageWorkSheet)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editTermConditions.isEdit = false"></i>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <div class="flex justify-content-center mt-2 gap-3" v-if="hasUserPermission(packageWorkSheet, false)">
        <Button id="btn-save" class="action-button" :label="$t('promos.save')" icon="pi pi-check" @click="onSave()" />
        <ViewPDFFile :quoteID="packageWorkSheet.sk" :disabled="disablePreviewPDF" isShowBtn></ViewPDFFile>
      </div>
    </section>
    <Dialog v-model:visible="visible" header=" " modal>
      <div v-if="isLoading" class="flex align-items-center flex-col modal progress-worksheet-modal">
        <h2 class="font-bold text-2xl">{{ $t('worksheet.package_call_progress') }}</h2>
        <p v-if="isPromotionModal">{{ $t('worksheet.retrieve_group') }}</p>
        <p v-else>{{ $t('worksheet.retrieve_tts') }}</p>
        <div class="w-3rem h-3rem rotage"><img class="w-full" :alt="$t('common.generic.loading')" :src="loadingIcon" /></div>
      </div>
      <div v-else-if="isSearchAdditional" class="flex align-items-center flex-col modal progress-worksheet-modal">
        <div class="font-bold text-2xl mb-2">{{ $t('worksheet.loading') }}</div>
        <div class="w-3rem h-3rem rotage"><img class="w-full" :alt="$t('common.generic.loading')" :src="loadingIcon" /></div>
      </div>
      <div v-else class="flex align-items-left flex-col modal">
        <p class="font-bold text-2xl text-center">{{ $t('worksheet.package_call_completed') }}</p>
        <div v-if="!isPromotionModal" class="text-lg mb-3">
          <div v-if="!newPrice">
            <i class="pi pi-times-circle text-red-700 font-bold text-xl mr-1"></i>
            <span>{{ $t('common.error') }} - {{ errorMessage ? errorMessage : $t('worksheet.un_retrieve_tts') }}</span>
          </div>
          <div v-else>
            <i class="pi pi-check-circle text-green-700 font-bold text-xl mr-1"></i>
            <span>{{ $t('worksheet.succ_retrieve_tts') }}</span>
          </div>
        </div>
        <div v-else class="text-lg mb-3">
          <div v-if="!newHcms">
            <i class="pi pi-times-circle text-red-700 font-bold text-xl mr-1"></i>
            <span>{{ $t('worksheet.un_retrieve_hcms') }}</span>
          </div>
          <div v-else>
            <i class="pi pi-check-circle text-green-700 font-bold text-xl mr-1"></i>
            <span v-if="isEmptyRespond">{{ $t('worksheet.empty_retrieve_hcms') }}</span>
            <span v-else>{{ $t('worksheet.succ_retrieve_hcms') }}</span>
          </div>
        </div>
        <div :class="['flex justify-content-between', !isPromotionModal && 'm-auto']">
          <Button id="buttonId1" v-if="!isPromotionModal" icon="pi pi-eye" :label="$t('common.buttons.view_pricing')" @click="previewNewPrice(newDataPricing, packageWorkSheet)" />
          <Button v-else icon="pi pi-eye" :label="$t('common.buttons.view')" @click="previewNewPromotion(newDataPricing)" />
          <Button v-if="isPromotionModal" icon="pi pi-check" :label="$t('common.buttons.close_apply')" @click="handleCloseAndApplyPromotion(packageWorkSheet)" />
        </div>
      </div>
    </Dialog>
  </div>
</template>
<style scoped lang="scss">
:deep() {
  .p-inputtextarea:disabled {
    opacity: 1;
    cursor: not-allowed;
  }
  .price-header {
    position: absolute;
    top: 0px;
    right: -3px;
    left: -3px;
    bottom: 0;
  }

  .background-opacity {
    background: rgba($color: #ddd, $alpha: 0.5);
  }

  .flight-detail {
    input {
      width: 100%;
    }
    table {
      td,
      th {
        padding: 1rem 0.75rem !important;
      }
    }
  }
}

:deep(.p-inputnumber .p-inputnumber-input) {
  width: 100%;
}
</style>
